import { tracked } from "@glimmer/tracking";
import Controller from "@ember/controller";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import { debounce } from "@ember/runloop";
import { service } from "@ember/service";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { uploadIcon } from "discourse/lib/uploads";
import UppyComposerUpload from "discourse/lib/uppy/composer-upload";
import { i18n } from "discourse-i18n";
import {
  buildCalendarDateBatches,
  isCalendarWorkLimitError,
  mergeCalendarEvents,
  splitCalendarDateBatch,
} from "../lib/dec-event-batches";
import { eventDetailUrl } from "../lib/dec-event-url";
import { publicationLabel } from "../lib/dec-hours";
import { uploadImageFile, uploadPreviewUrl } from "../lib/dec-image-upload";
import {
  normalizeHttpUrlInput as normalizedUrlInput,
  safeHttpUrl,
} from "../lib/dec-safe-url";

const EVENT_PREVIEW_SHOW_DELAY_MS = 220;
const EVENT_PREVIEW_HIDE_DELAY_MS = 320;
const EVENT_BATCH_DAYS = 7;
const CLIENT_EVENT_FILTERS_STORAGE_PREFIX =
  "discourse-events-calendar:client-event-filters";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizedStringSet(values) {
  return Array.from(
    new Set(asArray(values).map((value) => `${value || ""}`).filter(Boolean))
  ).sort();
}

function mergeRecordsById(existingRecords, incomingRecords) {
  const merged = [];
  const seen = new Set();

  [...asArray(existingRecords), ...asArray(incomingRecords)].forEach((record) => {
    const key = `${record?.id ?? record?.name ?? ""}`;
    if (!key || seen.has(key)) {
      return;
    }

    seen.add(key);
    merged.push(record);
  });

  return merged;
}

function extractErrorMessage(
  error,
  fallback = "Event could not be created. Check the event details and try again."
) {
  const responseJSON = error?.jqXHR?.responseJSON || error?.responseJSON;
  const errors = responseJSON?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join(" ");
  }

  if (typeof errors === "string") {
    return errors;
  }

  if (responseJSON?.error) {
    return responseJSON.error;
  }

  if (responseJSON?.message) {
    return responseJSON.message;
  }

  return fallback;
}

function externalCalendarProviderLabel(provider) {
  if (provider === "google") {
    return "Google Calendar";
  }

  if (provider === "edgeos") {
    return "EdgeOS";
  }

  return provider || "External calendar";
}

function externalCalendarConnectionView(connection, overrides = {}) {
  const lastSyncLabel = connection?.last_sync_at
    ? `Last synced ${formatDateTime(connection.last_sync_at)}`
    : "Not synced yet";
  const summary = connection?.sync_summary;
  const syncSummaryLabel =
    summary && Number.isInteger(summary.created)
      ? `${summary.rsvps_seen ?? 0} RSVPs found, ${summary.created} added, ${summary.removed ?? 0} removed`
      : "";

  return {
    ...connection,
    ...overrides,
    provider_label: externalCalendarProviderLabel(connection?.provider),
    is_edgeos: connection?.provider === "edgeos",
    is_google: connection?.provider === "google",
    sync_label: lastSyncLabel,
    sync_status_label: connection?.sync_enabled ? "Sync enabled" : "Sync disabled",
    sync_summary_label: syncSummaryLabel,
  };
}

function formatDateTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return safeDateFormat(
    date,
    {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    },
    `${fallbackDate(date)}, ${fallbackTime(date)}`
  );
}

function formatConflictRange(conflict) {
  const startsAt = conflict?.conflicting_starts_at || conflict?.starts_at;
  const endsAt = conflict?.conflicting_ends_at || conflict?.ends_at;

  if (!startsAt || !endsAt) {
    return "the selected time";
  }

  return `${formatDateTime(startsAt)} - ${formatDateTime(endsAt)}`;
}

function conflictEventPath(conflict) {
  return conflict?.event_id ? `/calendar/events/${conflict.event_id}` : "";
}

function conflictTitle(conflict) {
  if (!conflict) {
    return "";
  }

  if (conflict.private) {
    return "Private event";
  }

  return conflict.event_title || conflict.title || "Existing event";
}

function pad(value) {
  return `${value}`.padStart(2, "0");
}

function dateString(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function dateIsBefore(leftValue, rightValue) {
  return parseDateString(leftValue) < parseDateString(rightValue);
}

function defaultRecurrenceUntil(dateValue) {
  const date = parseDateString(dateValue);
  // Default a recurring event's finish date to 12 weeks out: comfortably past
  // the recurrence horizon and typical popup lengths, and always editable.
  date.setDate(date.getDate() + 12 * 7);
  return dateString(date);
}

const RECURRENCE_WEEKDAYS = [
  { code: "MO", label: "Mon", day: 1 },
  { code: "TU", label: "Tue", day: 2 },
  { code: "WE", label: "Wed", day: 3 },
  { code: "TH", label: "Thu", day: 4 },
  { code: "FR", label: "Fri", day: 5 },
  { code: "SA", label: "Sat", day: 6 },
  { code: "SU", label: "Sun", day: 0 },
];

function weekdayCodeForDate(dateValue) {
  const day = parseDateString(dateValue).getDay();
  return RECURRENCE_WEEKDAYS.find((weekday) => weekday.day === day)?.code || "MO";
}

function weeklyRrule({ byday, interval, until }) {
  const parts = [
    "FREQ=WEEKLY",
    `INTERVAL=${normalizedInterval(interval)}`,
    `BYDAY=${byday.join(",")}`,
  ];

  if (until) {
    const untilDate = parseDateString(until);
    untilDate.setHours(23, 59, 59, 0);
    parts.push(`UNTIL=${untilDate.toISOString().replace(/[-:]/g, "").replace(".000", "")}`);
  }

  return parts.join(";");
}

function monthString(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-01`;
}

function parseDateString(value) {
  if (!value) {
    return new Date();
  }

  const [year, month, day] = value.split("-").map((part) => parseInt(part, 10));

  if (!year || !month || !day) {
    return new Date();
  }

  return new Date(year, month - 1, day);
}

function nextQuarterHour(date = new Date()) {
  const next = new Date(date.getTime());
  const minutes = next.getMinutes();
  const remainder = minutes % 15;
  next.setMinutes(minutes + (remainder === 0 ? 15 : 15 - remainder), 0, 0);
  return next;
}

function timeString(date) {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function timeValueFromMinutes(totalMinutes) {
  const normalizedMinutes = ((totalMinutes % 1440) + 1440) % 1440;
  const hours = Math.floor(normalizedMinutes / 60);
  const minutes = normalizedMinutes % 60;

  return `${pad(hours)}:${pad(minutes)}`;
}

function defaultSchedule(date = new Date()) {
  const start = nextQuarterHour(date);
  const end = new Date(start.getTime() + 60 * 60 * 1000);

  return {
    eventDate: dateString(start),
    calendarMonth: monthString(start),
    startTime: timeString(start),
    endTime: timeString(end),
    endsNextDay: dateString(end) !== dateString(start),
  };
}

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

const TIMEZONE_ALIASES = {
  PDT: "America/Los_Angeles",
  PST: "America/Los_Angeles",
  MDT: "America/Denver",
  MST: "America/Denver",
  CDT: "America/Chicago",
  CST: "America/Chicago",
  EDT: "America/New_York",
  EST: "America/New_York",
};

function isValidTimeZone(timezone) {
  if (!timezone) {
    return false;
  }

  try {
    Intl.DateTimeFormat("en-US", { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

function normalizeTimeZone(timezone, fallback = browserTimeZone()) {
  const candidate = TIMEZONE_ALIASES[timezone] || timezone;

  if (isValidTimeZone(candidate)) {
    return candidate;
  }

  if (isValidTimeZone(fallback)) {
    return fallback;
  }

  return "UTC";
}

function timezoneOptions(selectedTimezone, defaultTimezone) {
  const normalizedSelectedTimezone = normalizeTimeZone(selectedTimezone, defaultTimezone);
  const normalizedDefaultTimezone = normalizeTimeZone(defaultTimezone, normalizedSelectedTimezone);
  const preferredZones = [
    normalizedSelectedTimezone,
    normalizedDefaultTimezone,
    browserTimeZone(),
    "UTC",
    "America/Los_Angeles",
    "America/New_York",
    "Europe/London",
    "Europe/Berlin",
    "Asia/Tokyo",
    "Australia/Sydney",
  ].filter(Boolean);
  const supportedZones =
    typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  const zones = [...new Set([...preferredZones, ...supportedZones])];

  return zones.map((zone) => ({
    value: zone,
    label: zone.replace(/_/g, " "),
    isSelected: zone === normalizedSelectedTimezone,
  }));
}

function timeLabel(value) {
  const [hours, minutes] = value.split(":").map((part) => parseInt(part, 10));

  const date = new Date(2000, 0, 1, hours, minutes);
  return safeDateFormat(
    date,
    {
      hour: "numeric",
      minute: "2-digit",
    },
    fallbackTime(date)
  );
}

function dateWithTime(dateValue, timeValue) {
  const date = parseDateString(dateValue);
  const [hours, minutes] = timeValue.split(":").map((part) => parseInt(part, 10));
  date.setHours(hours, minutes, 0, 0);
  return date;
}

function dateAndTimeAfter(dateValue, timeValue, minutes) {
  const date = dateWithTime(dateValue, timeValue);
  date.setMinutes(date.getMinutes() + minutes);

  return {
    date: dateString(date),
    time: timeString(date),
  };
}

function nextDayString(value) {
  const date = parseDateString(value);
  date.setDate(date.getDate() + 1);
  return dateString(date);
}

function addDays(value, days) {
  const date = value instanceof Date ? new Date(value.getTime()) : parseDateString(value);
  date.setDate(date.getDate() + days);
  return date;
}

function monthRange(value) {
  const month = parseDateString(value || dateString(new Date()));
  const startsAt = new Date(month.getFullYear(), month.getMonth(), 1);
  const endsAt = new Date(month.getFullYear(), month.getMonth() + 1, 1);

  return {
    start: dateString(startsAt),
    end: dateString(endsAt),
  };
}

function weekRange(value) {
  const date = parseDateString(value || dateString(new Date()));
  const startsAt = addDays(date, -date.getDay());
  const endsAt = addDays(startsAt, 7);

  return {
    start: dateString(startsAt),
    end: dateString(endsAt),
  };
}

function dayRange(value) {
  const startsAt = parseDateString(value || dateString(new Date()));
  const endsAt = addDays(startsAt, 1);

  return {
    start: dateString(startsAt),
    end: dateString(endsAt),
  };
}

function shortTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return safeDateFormat(
    date,
    {
      hour: "numeric",
      minute: "2-digit",
    },
    fallbackTime(date)
  );
}

function compactTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const minutes = date.getMinutes();
  const hours = date.getHours() % 12 || 12;
  const period = date.getHours() >= 12 ? "p" : "a";
  const minuteLabel = minutes === 0 ? "" : `:${pad(minutes)}`;

  return `${hours}${minuteLabel}${period}`;
}

function formatDateLabel(value) {
  const date = parseDateString(value);

  return safeDateFormat(
    date,
    {
      weekday: "short",
      month: "short",
      day: "numeric",
    },
    fallbackDate(date, false)
  );
}

function formatShortDateLabel(value, includeYear = false) {
  return fallbackDate(parseDateString(value), includeYear);
}

function formatRangeLabel(startsAt, endsAt) {
  const start = parseDateString(startsAt);
  const inclusiveEnd = addDays(endsAt, -1);
  const includeStartYear = start.getFullYear() !== inclusiveEnd.getFullYear();

  return `${formatShortDateLabel(startsAt, includeStartYear)} - ${formatShortDateLabel(
    dateString(inclusiveEnd),
    true
  )}`;
}

// Locale-aware date formatter with safe fallback. Uses the browser's
// resolved locale (falls back to undefined/system locale). On any error
// (no Intl, invalid options, invalid date) returns the caller's ASCII
// fallback so callers don't have to defend against this shape.
function safeDateFormat(date, options, fallback) {
  if (!date) {
    return fallback;
  }
  try {
    const dateObject = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(dateObject.getTime())) {
      return fallback;
    }
    return new Intl.DateTimeFormat(undefined, options).format(dateObject);
  } catch {
    return fallback;
  }
}

function fallbackDate(date, includeYear = true) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const base = `${months[date.getMonth()]} ${date.getDate()}`;
  return includeYear ? `${base}, ${date.getFullYear()}` : base;
}

function fallbackMonthYear(date) {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return `${months[date.getMonth()]} ${date.getFullYear()}`;
}

function fallbackTime(date) {
  let hours = date.getHours();
  const minutes = pad(date.getMinutes());
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${period}`;
}

function eventDateLabel(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return safeDateFormat(
    date,
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    },
    fallbackDate(date)
  );
}

function eventDateParts(value) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return {
      key: value || "unknown",
      dayName: "",
      dayNumber: "",
      monthLabel: value || "",
      fullLabel: value || "",
    };
  }

  return {
    key: dateString(date),
    dayName: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][date.getDay()],
    dayNumber: date.getDate(),
    monthLabel: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][
      date.getMonth()
    ],
    fullLabel: eventDateLabel(value),
  };
}

function sameLocalDate(left, right) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function eventTimeRange(startsAtValue, endsAtValue, allDay = false) {
  if (allDay) {
    return "All day";
  }

  if (!startsAtValue) {
    return "";
  }

  const startsAt = new Date(startsAtValue);
  const endsAt = new Date(endsAtValue || startsAtValue);

  if (Number.isNaN(startsAt.getTime())) {
    return "";
  }

  if (Number.isNaN(endsAt.getTime()) || sameLocalDate(startsAt, endsAt)) {
    return endsAtValue ? `${shortTime(startsAtValue)} - ${shortTime(endsAtValue)}` : shortTime(startsAtValue);
  }

  return `${formatDateTime(startsAtValue)} - ${formatDateTime(endsAtValue)}`;
}

function eventOverlapsDate(event, dateValue) {
  const startsAt = new Date(event.starts_at);
  const endsAt = new Date(event.ends_at || event.starts_at);

  if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
    return false;
  }

  const dayStart = parseDateString(dateValue);
  const dayEnd = new Date(dayStart.getTime());
  dayEnd.setDate(dayEnd.getDate() + 1);

  return startsAt < dayEnd && endsAt > dayStart;
}

function eventOverlapsRange(event, startsAt, endsAt) {
  if (!startsAt || !endsAt) {
    return true;
  }

  const eventStartsAt = new Date(event.starts_at);
  const eventEndsAt = new Date(event.ends_at || event.starts_at);

  if (Number.isNaN(eventStartsAt.getTime()) || Number.isNaN(eventEndsAt.getTime())) {
    return false;
  }

  return eventStartsAt < parseDateString(endsAt) && eventEndsAt > parseDateString(startsAt);
}

function localDayStart(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function minutesFromDayStart(date, dayValue) {
  const dayStart = parseDateString(dayValue);
  return Math.round((date.getTime() - dayStart.getTime()) / 60000);
}

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function snappedMinute(value, min, max) {
  const snapped = Math.round(value / 15) * 15;
  return clampNumber(snapped, min, max);
}

function timeGridMinuteFromPointer(event, element, gridStartMinutes, gridEndMinutes) {
  const rect = element.getBoundingClientRect();
  const ratio = rect.height > 0 ? (event.clientY - rect.top) / rect.height : 0;
  const rawMinute =
    gridStartMinutes + clampNumber(ratio, 0, 1) * (gridEndMinutes - gridStartMinutes);

  return snappedMinute(rawMinute, gridStartMinutes, gridEndMinutes);
}

function timeGridBlockStyle(startMinute, endMinute, gridStartMinutes, gridEndMinutes) {
  const gridDuration = gridEndMinutes - gridStartMinutes;
  const topPercent = ((startMinute - gridStartMinutes) / gridDuration) * 100;
  const heightPercent = ((endMinute - startMinute) / gridDuration) * 100;

  return [
    `top: ${topPercent.toFixed(3)}%`,
    `height: calc(${heightPercent.toFixed(3)}% - 2px)`,
  ].join("; ");
}

function timeRangeLabelFromMinutes(startMinute, endMinute) {
  return `${timeLabel(timeValueFromMinutes(startMinute))} - ${timeLabel(
    timeValueFromMinutes(endMinute)
  )}`;
}

function normalizeDraftRange(draft, gridStartMinutes, gridEndMinutes) {
  if (!draft) {
    return null;
  }

  const startMinute = clampNumber(
    Math.min(draft.startMinute, draft.endMinute),
    gridStartMinutes,
    gridEndMinutes - 15
  );
  const endMinute = clampNumber(
    Math.max(draft.startMinute, draft.endMinute),
    startMinute + 15,
    gridEndMinutes
  );

  return { startMinute, endMinute };
}

function timeGridDraftItem(draft, gridStartMinutes, gridEndMinutes) {
  const range = normalizeDraftRange(draft, gridStartMinutes, gridEndMinutes);

  if (!range) {
    return null;
  }

  return {
    ...range,
    className: [
      "discourse-events-calendar__time-grid-create-selection",
      draft.isCommitted
        ? "discourse-events-calendar__time-grid-create-selection--pending"
        : "",
    ]
      .filter(Boolean)
      .join(" "),
    isCommitted: draft.isCommitted === true,
    label: timeRangeLabelFromMinutes(range.startMinute, range.endMinute),
    style: timeGridBlockStyle(
      range.startMinute,
      range.endMinute,
      gridStartMinutes,
      gridEndMinutes
    ),
  };
}

function eventIsAllDayForDate(event, dayValue) {
  const startsAt = new Date(event.starts_at);
  const endsAt = new Date(event.ends_at || event.starts_at);

  if (event.all_day === true) {
    return true;
  }

  if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
    return false;
  }

  const dayStart = parseDateString(dayValue);
  const dayEnd = addDays(dayStart, 1);
  const nearlyFullDay = endsAt.getTime() - startsAt.getTime() >= 23 * 60 * 60000;

  return nearlyFullDay && startsAt <= dayStart && endsAt >= dayEnd;
}

function timeGridEventItems(events, dayValue, gridStartMinutes, gridEndMinutes) {
  const minVisualDurationMinutes = 30;
  const dayStart = parseDateString(dayValue);
  const dayEnd = addDays(dayStart, 1);
  const visibleStart = new Date(dayStart.getTime() + gridStartMinutes * 60000);
  const visibleEnd = new Date(dayStart.getTime() + gridEndMinutes * 60000);
  const gridDuration = gridEndMinutes - gridStartMinutes;
  const items = events
    .map((event) => {
      const startsAt = new Date(event.starts_at);
      const endsAt = new Date(event.ends_at || event.starts_at);

      if (
        Number.isNaN(startsAt.getTime()) ||
        Number.isNaN(endsAt.getTime()) ||
        startsAt >= dayEnd ||
        endsAt <= dayStart ||
        endsAt <= visibleStart ||
        startsAt >= visibleEnd
      ) {
        return null;
      }

      const clampedStart = startsAt < visibleStart ? visibleStart : startsAt;
      const clampedEnd = endsAt > visibleEnd ? visibleEnd : endsAt;
      const startMinute = Math.max(
        gridStartMinutes,
        minutesFromDayStart(clampedStart, dayValue)
      );
      const endMinute = Math.min(
        gridEndMinutes,
        minutesFromDayStart(clampedEnd, dayValue)
      );

      return {
        ...event,
        gridStartMinute: startMinute,
        gridEndMinute: Math.max(startMinute + 15, endMinute),
        visualGridEndMinute: Math.min(
          gridEndMinutes,
          Math.max(endMinute, startMinute + minVisualDurationMinutes)
        ),
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (left.gridStartMinute === right.gridStartMinute) {
        return right.gridEndMinute - left.gridEndMinute;
      }

      return left.gridStartMinute - right.gridStartMinute;
    });

  const groups = [];

  items.forEach((item) => {
    const currentGroup = groups[groups.length - 1];

    if (!currentGroup || item.gridStartMinute >= currentGroup.endMinute) {
      groups.push({
        endMinute: item.visualGridEndMinute,
        items: [item],
      });
      return;
    }

    currentGroup.endMinute = Math.max(currentGroup.endMinute, item.visualGridEndMinute);
    currentGroup.items.push(item);
  });

  return groups.flatMap((group) => {
    const laneEnds = [];

    group.items.forEach((item) => {
      let laneIndex = laneEnds.findIndex((endMinute) => endMinute <= item.gridStartMinute);

      if (laneIndex === -1) {
        laneIndex = laneEnds.length;
      }

      laneEnds[laneIndex] = item.visualGridEndMinute;
      item.laneIndex = laneIndex;
    });

    const laneCount = Math.max(1, laneEnds.length);

    return group.items.map((item) => {
      const topPercent = ((item.gridStartMinute - gridStartMinutes) / gridDuration) * 100;
      const heightPercent =
        ((item.visualGridEndMinute - item.gridStartMinute) / gridDuration) * 100;

      return {
        ...item,
        timeGridStyle: [
          `top: ${topPercent.toFixed(3)}%`,
          `height: calc(${heightPercent.toFixed(3)}% - 2px)`,
          `left: calc(${item.laneIndex} * (100% / ${laneCount}) + 0.12rem)`,
          `width: calc((100% / ${laneCount}) - 0.24rem)`,
        ].join("; "),
      };
    });
  });
}

function eventMatchesSearch(event, query) {
  const normalizedQuery = query?.trim().toLowerCase();

  if (!normalizedQuery) {
    return true;
  }

  return [
    event.title,
    event.description,
    event.location,
    event.place?.name,
    event.place?.address,
    event.room?.name,
    event.room?.location,
    event.category?.name,
    ...(event.tags || []).map((tag) => tag.name),
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalizedQuery));
}

function eventSortValuesForDate(event, dayValue) {
  const startsAt = new Date(event.starts_at);
  const endsAt = new Date(event.ends_at || event.starts_at);
  const dayStart = parseDateString(dayValue);

  if (Number.isNaN(startsAt.getTime())) {
    return [Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER];
  }

  return [
    Math.max(startsAt.getTime(), dayStart.getTime()),
    startsAt.getTime(),
    Number.isNaN(endsAt.getTime()) ? startsAt.getTime() : endsAt.getTime(),
  ];
}

function sortEventsForDate(events, dayValue) {
  return [...events].sort((left, right) => {
    const leftValues = eventSortValuesForDate(left, dayValue);
    const rightValues = eventSortValuesForDate(right, dayValue);

    for (let index = 0; index < leftValues.length; index++) {
      if (leftValues[index] !== rightValues[index]) {
        return leftValues[index] - rightValues[index];
      }
    }

    return `${left.title || ""}`.localeCompare(`${right.title || ""}`);
  });
}

function locationUrl(locationRecord) {
  const value = locationRecord?.slug || locationRecord?.id || locationRecord?.record_id || "";
  return value ? `/calendar/locations/${encodeURIComponent(value)}` : "";
}

function locationValue(location) {
  return `${location?.record_id || location?.id || location?.slug || location?.name || ""}`;
}

function locationRecordId(location) {
  return location?.record_id || location?.id;
}

function roomUrl(room) {
  return room?.id ? `/calendar/rooms/${room.id}` : "";
}

function eventIsPast(event) {
  if (event.is_past === true) {
    return true;
  }

  const endsAt = new Date(event.ends_at);
  return !Number.isNaN(endsAt.getTime()) && endsAt < new Date();
}

function eventStatusLabel(event, isPast) {
  if (event.status === "draft") {
    return publicationLabel("draft");
  }

  if (event.status === "pending_approval") {
    return "Pending approval";
  }

  if (event.status === "rejected") {
    return "Rejected";
  }

  if (event.status === "canceled") {
    return "Canceled";
  }

  if (event.status === "archived") {
    return "Archived";
  }

  return isPast ? "Past" : event.is_ongoing ? "In progress" : "Upcoming";
}

function eventRsvpSummary(event) {
  if (event.rsvp_mode === "external" && event.external_rsvp_url) {
    return "";
  }

  if (!event.rsvp_ui_enabled) {
    return "";
  }

  const going = event.rsvp_counts?.going || 0;
  const interested = event.rsvp_counts?.interested || 0;
  const waitlisted = event.rsvp_counts?.waitlisted || 0;
  const capacity = event.capacity ? ` / ${event.capacity}` : "";
  const interestedLabel = interested > 0 ? ` · ${interested} interested` : "";
  const waitlistedLabel = waitlisted > 0 ? ` · ${waitlisted} waitlisted` : "";

  return `${going}${capacity} going${interestedLabel}${waitlistedLabel}`;
}

// Event locations are stored as free text like "The Hub - Wellness Space - 405
// Healdsburg Ave" (venue · room · address). In the list we only want the venue
// and room, so drop a trailing street-address segment (one that starts with a
// number).
function trimVenueLocation(location) {
  if (!location) {
    return location;
  }

  const parts = location
    .split(/\s*[–-]\s*/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length > 1 && /^\d/.test(parts[parts.length - 1])) {
    parts.pop();
  }

  return parts.join(" – ");
}

function rsvpStatusLabel(status) {
  switch (status) {
    case "going":
      return "Going";
    case "interested":
      return "Interested";
    case "not_going":
      return "Not going";
    case "waitlisted":
      return "Waitlisted";
    default:
      return "";
  }
}

function plainTextExcerpt(value, limit = 190) {
  const text = `${value || ""}`
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*\]\([^)]+\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/<[^>]*>/g, " ")
    .replace(/[#>*_`~|\[\]()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (text.length <= limit) {
    return text;
  }

  return `${text.slice(0, limit - 3).trim()}...`;
}

function recurrenceLabel(rule) {
  switch (rule) {
    case "daily":
      return "Daily";
    case "weekly":
      return "Weekly";
    case "monthly":
      return "Monthly";
    case "yearly":
      return "Yearly";
    default:
      return "None";
  }
}

function recurrenceUnit(rule, interval) {
  const singular =
    {
      daily: "day",
      weekly: "week",
      monthly: "month",
      yearly: "year",
    }[rule] || "period";

  return interval === 1 ? singular : `${singular}s`;
}

function normalizedInterval(value) {
  const interval = parseInt(value, 10);
  return Number.isFinite(interval) && interval > 0 ? interval : 1;
}

function editorValue(valueOrEvent) {
  if (typeof valueOrEvent === "string") {
    return valueOrEvent;
  }

  return valueOrEvent?.target?.value || "";
}

function sortEvents(events, filter) {
  return [...events].sort((left, right) => {
    const leftTime = new Date(left.starts_at).getTime() || 0;
    const rightTime = new Date(right.starts_at).getTime() || 0;
    return filter === "past" ? rightTime - leftTime : leftTime - rightTime;
  });
}

function previewEventKey(event) {
  return `${event?.id || ""}::${event?.starts_at || ""}`;
}

function readStorageJson(key) {
  if (typeof window === "undefined" || !window.localStorage) {
    return {};
  }

  try {
    return JSON.parse(window.localStorage.getItem(key) || "{}") || {};
  } catch {
    return {};
  }
}

function writeStorageJson(key, value) {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Browser storage can be unavailable in private contexts. Filters still
    // work for the current controller lifetime.
  }
}

function eventCategoryKey(event) {
  return `${event?.category_id || event?.category?.id || ""}`;
}

function eventTagKeys(event) {
  return asArray(event?.tags)
    .map((tag) => `${tag?.id || ""}`)
    .filter(Boolean);
}

function canPreviewRsvp(event, currentUser) {
  if (!currentUser?.id || !event?.rsvp_ui_enabled || !event?.rsvp_enabled) {
    return false;
  }

  if (event.rsvp_mode === "external") {
    return false;
  }

  if (["canceled", "draft", "archived"].includes(event.status)) {
    return false;
  }

  return !["going", "waitlisted"].includes(event.current_user_rsvp_status);
}

function canPreviewExternalRsvp(event) {
  if (!safeHttpUrl(event?.external_rsvp_url) || event.rsvp_mode !== "external") {
    return false;
  }

  return !["canceled", "draft", "archived"].includes(event.status);
}

function previewRsvpLabel(event) {
  return event?.current_user_rsvp_status === "interested"
    ? "Change to going"
    : "RSVP going";
}

function normalizeView(view) {
  return ["month", "week", "day", "venue", "agenda"].includes(view) ? view : "month";
}

function tagFilterIds(value) {
  return `${value || ""}`
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function idFilterValues(value) {
  return tagFilterIds(value)
    .map((item) => parseInt(item, 10))
    .filter((item) => item > 0);
}

function normalizedIdString(value) {
  const id = parseInt(value, 10);
  return id > 0 ? `${id}` : "";
}

function categoryIdsForRecord(record) {
  const ids = [];
  const pushId = (value) => {
    const id = normalizedIdString(value);
    if (id && !ids.includes(id)) {
      ids.push(id);
    }
  };

  pushId(record?.category_id);
  pushId(record?.category?.id);
  asArray(record?.category_ids).forEach(pushId);

  const place = record?.place;
  pushId(place?.category_id);
  pushId(place?.category?.id);
  asArray(place?.category_ids).forEach(pushId);

  const locationRecord = record?.location_record;
  pushId(locationRecord?.category_id);
  pushId(locationRecord?.category?.id);
  asArray(locationRecord?.category_ids).forEach(pushId);
  pushId(locationRecord?.place?.category_id);
  pushId(locationRecord?.place?.category?.id);
  asArray(locationRecord?.place?.category_ids).forEach(pushId);

  return ids;
}

function normalizeTagName(value) {
  return `${value || ""}`
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
}

function tagNames(value) {
  return `${value || ""}`
    .split(",")
    .map((name) => normalizeTagName(name))
    .filter(Boolean)
    .filter((name, index, names) => names.indexOf(name) === index);
}

function participantNames(value) {
  return `${value || ""}`
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
}

function participantRoleRows(value, role, positionOffset = 0) {
  return participantNames(value).map((displayName, index) => ({
    role,
    display_name: displayName,
    position: positionOffset + index,
  }));
}

function currentUserParticipantName(currentUser) {
  return currentUser?.username ? `@${currentUser.username}` : "";
}

function roleSearchTerm(value) {
  return `${value || ""}`
    .split(",")
    .pop()
    .trim()
    .replace(/^@/, "");
}

function normalizeUserOption(user) {
  if (!user?.username) {
    return null;
  }

  return {
    username: user.username,
    name: user.name || "",
    value: `@${user.username}`,
    label: user.name ? `${user.name} (@${user.username})` : `@${user.username}`,
  };
}

function normalizePlaceOption(place) {
  if (!place?.id || !place?.name) {
    return null;
  }

  const address = place.address || place.formatted_address || "";
  return {
    ...place,
    id: place.id,
    name: place.name,
    address,
    label: [place.name, address].filter(Boolean).join(" - "),
  };
}

function normalizeVenueSuggestion(suggestion) {
  if (!suggestion?.id || !suggestion?.title) {
    return null;
  }

  const matchReasons = asArray(suggestion.match_reasons).filter(Boolean);
  return {
    ...suggestion,
    id: suggestion.id,
    title: suggestion.title,
    subtitle: suggestion.subtitle || suggestion.address || "",
    publicLocation: suggestion.public_location || suggestion.address || suggestion.title,
    imageUrl: suggestion.image_url || "",
    availabilityLabel: suggestion.availability_label || "Available",
    matchReasons,
    primaryReason: matchReasons[0] || suggestion.availability_label || "Available",
    capacityLabel: suggestion.capacity ? `Capacity ${suggestion.capacity}` : "Capacity not listed",
    isRoom: suggestion.type === "room",
  };
}

function viewTabClass(isActive) {
  return [
    "btn",
    "btn-small",
    "discourse-events-calendar__view-tab",
    isActive ? "discourse-events-calendar__view-tab--active" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function normalizeScope(scope) {
  return ["all", "my_rsvps", "organizing"].includes(scope) ? scope : "all";
}

function normalizeRsvpStatus(status) {
  return ["", "going", "interested", "waitlisted", "not_going"].includes(status) ? status : "";
}

function normalizeEventVisibility(visibility) {
  return ["", "public_visibility", "staff_visibility", "private_visibility"].includes(visibility)
    ? visibility
    : "";
}

function visibilityModeFromFields(visibility, hidden) {
  if (visibility === "private_visibility") {
    return "private";
  }

  if (visibility === "staff_visibility") {
    return "staff";
  }

  return hidden ? "unlisted" : "public";
}

function visibilityValueForMode(mode) {
  if (mode === "private") {
    return "private_visibility";
  }

  if (mode === "staff") {
    return "staff_visibility";
  }

  return "public_visibility";
}

function hiddenValueForMode(mode) {
  return ["private", "unlisted"].includes(mode);
}

function normalizeConversationMode(mode) {
  return ["none", "discussion", "chat"].includes(mode) ? mode : "discussion";
}

function normalizeVenueType(type) {
  return ["tbd", "existing", "custom", "virtual"].includes(type) ? type : "tbd";
}

function looksLikeUrl(value) {
  return /^(https?:\/\/|www\.|[a-z0-9-]+\.[a-z]{2,})(\S*)$/i.test(`${value || ""}`.trim());
}

export default class DiscourseEventsCalendarController extends Controller {
  @service currentUser;
  @service router;
  @service site;
  @service siteSettings;

  queryParams = [
    "filter",
    "start",
    "end",
    "q",
    "room_id",
    "location_id",
    "category_id",
    "tag_id",
    "tag_ids",
    "owner_id",
    "place_id",
    "place_slug",
    "place_name",
    "event_visibility",
    "scope",
    "rsvp_status",
    "view",
    "create",
    "external_calendars",
    "subscribe",
  ];

  @tracked filter = "upcoming";
  @tracked start = null;
  @tracked end = null;
  @tracked q = "";
  @tracked searchQuery = "";
  @tracked room_id = "";
  @tracked location_id = "";
  @tracked category_id = "";
  @tracked tag_id = "";
  @tracked tag_ids = "";
  @tracked owner_id = "";
  @tracked place_id = "";
  @tracked place_slug = "";
  @tracked place_name = "";
  @tracked event_visibility = "";
  @tracked scope = "all";
  @tracked rsvp_status = "";
  @tracked external_calendars = "";
  @tracked subscribe = "";
  @tracked browserMonth = "";
  @tracked selectedBrowserDate = "";
  @tracked openMonthMoreDate = "";
  @tracked isTagFilterMenuOpen = false;
  @tracked timeGridCanScrollLeft = false;
  @tracked timeGridCanScrollRight = true;
  @tracked timeGridDraft = null;
  @tracked view = "agenda";
  @tracked create = "";
  @tracked title = "";
  @tracked imageUpload = null;
  @tracked imageUploadId = "";
  @tracked imageUploadError = "";
  @tracked isUploadingImage = false;
  @tracked showAiImagePrompt = false;
  @tracked aiImagePrompt = "";
  @tracked pendingAiImageUpload = null;
  @tracked isComposingAiImagePrompt = false;
  @tracked isGeneratingAiImage = false;
  @tracked eventDate = "";
  @tracked calendarMonth = "";
  @tracked startTime = "";
  @tracked endTime = "";
  @tracked endsNextDay = false;
  @tracked venueType = "tbd";
  @tracked location = "";
  @tracked meetingUrl = "";
  @tracked customLocationUrl = "";
  @tracked placeQuery = "";
  @tracked placeId = "";
  @tracked selectedPlaceCategoryIds = [];
  @tracked placeOptions = [];
  @tracked isLoadingPlaces = false;
  @tracked showVenueSearch = false;
  @tracked showVenueFinder = false;
  @tracked isLoadingVenueSuggestions = false;
  @tracked venueSuggestions = [];
  @tracked venueFinderError = "";
  @tracked venueFinderMissingFields = [];
  @tracked selectedLocationId = "";
  @tracked selectedRoomId = "";
  @tracked selectedCategoryId = "";
  @tracked createTagIds = "";
  @tracked tagNamesCsv = "";
  @tracked newTagName = "";
  @tracked isCreateTagMenuOpen = false;
  @tracked hostNames = "";
  @tracked coHostNames = "";
  @tracked speakerNames = "";
  @tracked withNames = "";
  @tracked showCreatePeopleDetails = false;
  @tracked roleUserOptions = [];
  @tracked isLoadingRoleUsers = false;
  @tracked inviteUsernames = "";
  @tracked inviteGroupNames = "";
  @tracked roomLocationApplied = "";
  @tracked description = "";
  @tracked capacity = "";
  @tracked visibility = "public";
  @tracked timezone = "";
  @tracked recurrenceRule = "none";
  @tracked recurrenceInterval = "1";
  @tracked recurrenceUntil = "";
  @tracked recurrenceByday = "";
  @tracked allDay = false;
  @tracked rsvpEnabled = true;
  @tracked rsvpMode = "native";
  @tracked externalRsvpUrl = "";
  @tracked rsvpAllowInterested = true;
  @tracked createTopic = true;
  @tracked createChat = false;
  @tracked conversationMode = "discussion";
  @tracked isSaving = false;
  @tracked createError = "";
  @tracked createNotice = "";
  @tracked createConflict = null;
  @tracked createConflictAlternatives = [];
  @tracked roomAvailabilityStatus = "";
  @tracked roomAvailabilityMessage = "";
  @tracked roomAvailabilityConflict = null;
  @tracked isCreateFormExpanded = false;
  @tracked isCreateMenuOpen = false;
  @tracked showRoomForm = false;
  @tracked roomName = "";
  @tracked roomLocation = "";
  @tracked roomImageUpload = null;
  @tracked roomImageUploadId = "";
  @tracked roomImageUploadError = "";
  @tracked isUploadingRoomImage = false;
  @tracked roomCapacity = "";
  @tracked roomBookingWindowDays = "";
  @tracked roomBufferMinutes = "";
  @tracked isSavingRoom = false;
  @tracked roomError = "";
  @tracked isLoadingEvents = false;
  @tracked eventsLoadError = "";
  @tracked eventsLoadIsPartial = false;
  @tracked eventsLoadHardLimited = false;
  @tracked eventsLoadCompletedBatches = 0;
  @tracked eventsLoadTotalBatches = 0;
  @tracked eventsLoadCanRetry = false;
  @tracked selectedPreviewEvent = null;
  @tracked eventPreviewStyle = "";
  @tracked eventPreviewSource = "";
  @tracked eventPreviewDate = "";
  @tracked hiddenEventKeys = [];
  @tracked mutedCategoryIds = [];
  @tracked mutedTagIds = [];
  @tracked isSavingPreviewRsvp = false;
  @tracked previewRsvpSavingKey = "";
  @tracked loadingMonthMoreDate = "";
  @tracked showAdvancedFilters = false;
  @tracked isMobileFiltersExpanded = false;
  @tracked isSavingSubscription = false;
  @tracked subscriptionNotice = "";
  @tracked subscriptionError = "";
  @tracked subscriptionLinks = null;
  @tracked showExternalCalendarPanel = false;
  @tracked externalCalendarConnections = [];
  @tracked isLoadingExternalCalendars = false;
  @tracked externalCalendarError = "";
  @tracked externalCalendarNotice = "";
  @tracked edgeosConnectionsChecked = false;
  @tracked edgeosRsvpHintDismissed = false;
  @tracked showEdgeosConnectForm = false;
  @tracked edgeosApiKeyInput = "";
  @tracked isConnectingEdgeos = false;
  @tracked googleCalendarConfigured = false;
  eventsRequestId = 0;
  eventBatchQueue = [];
  eventBatchLoadSignature = "";
  roomAvailabilityRequestId = 0;
  placeRequestId = 0;
  placePrefillRequestId = 0;
  venueFinderRequestId = 0;
  roleUserRequestId = 0;
  lastEventsLoadSignature = "";
  pendingEventsLoadSignature = "";
  descriptionFileUploadElementId = "event-description-file-uploader";
  eventDescriptionUpload = null;
  eventDescriptionUploadElement = null;
  eventDescriptionUploadSetup = false;
  searchDebounceTimer = null;
  eventPreviewShowTimer = null;
  eventPreviewShowKey = "";
  eventPreviewDismissTimer = null;
  eventPreviewTouchStartX = null;
  eventTouchPreviewTimer = null;
  eventTouchStartX = null;
  eventTouchStartY = null;
  eventTouchPreviewKey = "";
  eventTouchLongPressFired = false;
  suppressedEventClickKey = "";
  monthMorePreviewScrollTop = null;
  eventsCacheSource = null;
  eventsCacheFilter = "";
  eventsCacheClientFilterSignature = "";
  eventsCacheLength = -1;
  eventsCacheResult = [];
  eventsByDateCacheEvents = null;
  eventsByDateCacheStart = "";
  eventsByDateCacheEnd = "";
  eventsByDateCacheResult = null;

  constructor() {
    super(...arguments);
    this.resetScheduleFields();
    this.loadClientEventPreferences();
  }

  get clientEventFilterStorageKey() {
    return `${CLIENT_EVENT_FILTERS_STORAGE_PREFIX}:${this.currentUser?.id || "anonymous"}`;
  }

  get clientEventFilterSignature() {
    return [
      this.hiddenEventKeys.join(","),
      this.mutedCategoryIds.join(","),
      this.mutedTagIds.join(","),
    ].join("::");
  }

  get hasClientEventFilters() {
    return (
      this.hiddenEventKeys.length > 0 ||
      this.mutedCategoryIds.length > 0 ||
      this.mutedTagIds.length > 0
    );
  }

  get hasHiddenEvents() {
    return this.hiddenEventKeys.length > 0;
  }

  get hiddenEventCount() {
    return this.hiddenEventKeys.length;
  }

  get hideVisibleEventsDisabled() {
    return this.events.length === 0;
  }

  get notInterestedSummaryLabel() {
    const parts = [];

    if (this.hiddenEventKeys.length > 0) {
      parts.push(`${this.hiddenEventKeys.length} hidden`);
    }

    if (this.mutedCategoryIds.length > 0) {
      parts.push(`${this.mutedCategoryIds.length} categories`);
    }

    if (this.mutedTagIds.length > 0) {
      parts.push(`${this.mutedTagIds.length} tags`);
    }

    return parts.join(" · ");
  }

  get mutedCategoryOptions() {
    const mutedCategoryIds = new Set(this.mutedCategoryIds);

    return this.categories.map((category) => ({
      id: category.id,
      name: category.name,
      isMuted: mutedCategoryIds.has(`${category.id}`),
    }));
  }

  get mutedTagOptions() {
    const mutedTagIds = new Set(this.mutedTagIds);

    return this.tags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      isMuted: mutedTagIds.has(`${tag.id}`),
    }));
  }

  loadClientEventPreferences() {
    const preferences = readStorageJson(this.clientEventFilterStorageKey);

    this.hiddenEventKeys = normalizedStringSet(preferences.hiddenEventKeys);
    this.mutedCategoryIds = normalizedStringSet(preferences.mutedCategoryIds);
    this.mutedTagIds = normalizedStringSet(preferences.mutedTagIds);
  }

  saveClientEventPreferences() {
    writeStorageJson(this.clientEventFilterStorageKey, {
      hiddenEventKeys: this.hiddenEventKeys,
      mutedCategoryIds: this.mutedCategoryIds,
      mutedTagIds: this.mutedTagIds,
    });
  }

  resetEventsCaches() {
    this.eventsCacheSource = null;
    this.eventsCacheFilter = "";
    this.eventsCacheClientFilterSignature = "";
    this.eventsCacheLength = -1;
    this.eventsCacheResult = [];
    this.eventsByDateCacheEvents = null;
    this.eventsByDateCacheResult = null;
  }

  eventPassesClientFilters(event) {
    if (!this.hasClientEventFilters) {
      return true;
    }

    if (this.hiddenEventKeys.includes(previewEventKey(event))) {
      return false;
    }

    const categoryKey = eventCategoryKey(event);
    if (categoryKey && this.mutedCategoryIds.includes(categoryKey)) {
      return false;
    }

    if (
      this.mutedTagIds.length > 0 &&
      eventTagKeys(event).some((tagId) => this.mutedTagIds.includes(tagId))
    ) {
      return false;
    }

    return true;
  }

  get events() {
    const source = this.model?.events || this.model;
    const events = asArray(source).filter(
      (event) => event?.id !== undefined && event?.id !== null
    );
    const clientFilterSignature = this.clientEventFilterSignature;

    if (
      this.eventsCacheSource === source &&
      this.eventsCacheFilter === this.activeFilter &&
      this.eventsCacheClientFilterSignature === clientFilterSignature &&
      this.eventsCacheLength === events.length
    ) {
      return this.eventsCacheResult;
    }

    const result = sortEvents(
      events
        .filter((event) => this.eventPassesClientFilters(event))
        .map((event) => {
          const isPast = eventIsPast(event);
          const room = event.room
            ? {
                ...event.room,
                detailUrl: roomUrl(event.room),
                locationUrl: locationUrl(event.room.location_record),
              }
            : null;

          let stateClass = "";

          if (event.status === "canceled") {
            stateClass = "discourse-events-calendar__event--canceled";
          } else if (event.status === "draft") {
            stateClass = "discourse-events-calendar__event--draft";
          } else if (isPast) {
            stateClass = "discourse-events-calendar__event--past";
          }

          return {
            ...event,
            external_rsvp_url: safeHttpUrl(event.external_rsvp_url),
            room,
            isPast,
            stateClass,
            statusLabel: eventStatusLabel(event, isPast),
            rsvpSummary: eventRsvpSummary(event),
            previewDescription:
              event.description_excerpt || plainTextExcerpt(event.description),
            organizerName: event.owner?.username ? `@${event.owner.username}` : "",
            currentUserRsvpLabel: rsvpStatusLabel(event.current_user_rsvp_status),
            canPreviewRsvp: canPreviewRsvp(event, this.currentUser),
            canPreviewExternalRsvp: canPreviewExternalRsvp(event),
            previewRsvpLabel: previewRsvpLabel(event),
            scheduleMeta: [
              event.category?.name,
              event.place?.name,
              room?.name,
              event.location && ![event.place?.name, room?.name].includes(event.location)
                ? trimVenueLocation(event.location)
                : null,
              eventRsvpSummary(event),
            ].filter(Boolean),
            displayDate: eventDateLabel(event.starts_at),
            displayTimeRange: eventTimeRange(
              event.starts_at,
              event.ends_at,
              event.all_day
            ),
            monthTime: event.all_day ? "" : compactTime(event.starts_at),
            hoverTitle: [
              event.title,
              eventTimeRange(event.starts_at, event.ends_at, event.all_day),
              event.place?.name,
              room?.name,
              event.location && ![event.place?.name, room?.name].includes(event.location)
                ? trimVenueLocation(event.location)
                : null,
              event.category?.name,
              eventRsvpSummary(event),
            ]
              .filter(Boolean)
              .join("\n"),
            detailUrl: eventDetailUrl(event),
            locationUrl: locationUrl(event.location_record),
          };
        }),
      this.activeFilter
    );

    this.eventsCacheSource = source;
    this.eventsCacheFilter = this.activeFilter;
    this.eventsCacheClientFilterSignature = clientFilterSignature;
    this.eventsCacheLength = events.length;
    this.eventsCacheResult = result;
    this.eventsByDateCacheEvents = null;

    return result;
  }

  eventsByDateForRange(startsAt, endsAt) {
    const events = this.events;

    if (
      this.eventsByDateCacheEvents === events &&
      this.eventsByDateCacheStart === startsAt &&
      this.eventsByDateCacheEnd === endsAt &&
      this.eventsByDateCacheResult
    ) {
      return this.eventsByDateCacheResult;
    }

    const rangeStart = parseDateString(startsAt);
    const rangeEnd = parseDateString(endsAt);
    const eventsByDate = new Map();

    events.forEach((event) => {
      const eventStartsAt = new Date(event.starts_at);
      const eventEndsAt = new Date(event.ends_at || event.starts_at);

      if (
        Number.isNaN(eventStartsAt.getTime()) ||
        Number.isNaN(eventEndsAt.getTime()) ||
        eventStartsAt >= rangeEnd ||
        eventEndsAt <= rangeStart
      ) {
        return;
      }

      const firstDay = new Date(
        Math.max(rangeStart.getTime(), localDayStart(eventStartsAt).getTime())
      );
      const lastExclusive = new Date(Math.min(rangeEnd.getTime(), eventEndsAt.getTime()));

      for (
        let cursor = firstDay;
        cursor < lastExclusive;
        cursor = addDays(cursor, 1)
      ) {
        const day = dateString(cursor);
        const dayEvents = eventsByDate.get(day) || [];
        dayEvents.push(event);
        eventsByDate.set(day, dayEvents);
      }
    });

    eventsByDate.forEach((dayEvents, day) => {
      eventsByDate.set(day, sortEventsForDate(dayEvents, day));
    });

    this.eventsByDateCacheEvents = events;
    this.eventsByDateCacheStart = startsAt;
    this.eventsByDateCacheEnd = endsAt;
    this.eventsByDateCacheResult = eventsByDate;

    return eventsByDate;
  }

  get agendaEvents() {
    if (!this.selectedBrowserDate) {
      const range = monthRange(this.activeBrowserMonth);
      return this.events.filter((event) => eventOverlapsRange(event, range.start, range.end));
    }

    return this.events.filter((event) => eventOverlapsDate(event, this.selectedBrowserDate));
  }

  get agendaGroups() {
    const groupsByDate = new Map();

    this.agendaEvents.forEach((event) => {
      const dateParts = eventDateParts(event.starts_at);
      const existingGroup = groupsByDate.get(dateParts.key);

      if (existingGroup) {
        existingGroup.events.push(event);
        return;
      }

      groupsByDate.set(dateParts.key, {
        ...dateParts,
        events: [event],
      });
    });

    return Array.from(groupsByDate.values());
  }

  get rooms() {
    return this.model?.rooms || [];
  }

  get locations() {
    return this.model?.locations || [];
  }

  get hasLocations() {
    return this.locations.length > 0;
  }

  get hasCategoryScopedVenues() {
    return this.locations.some((location) => categoryIdsForRecord(location).length > 0);
  }

  get filteredLocations() {
    return this.locations.filter((location) => this.venueMatchesSelectedCategory(location));
  }

  get hasLocationOptions() {
    return this.filteredLocations.length > 0;
  }

  get locationOptions() {
    return this.filteredLocations.map((location) => ({
      ...location,
      value: locationValue(location),
      isSelected: locationValue(location) === this.selectedLocationId,
    }));
  }

  get locationFilterOptions() {
    return this.locations.map((location) => ({
      ...location,
      value: locationValue(location),
      isSelected: locationValue(location) === `${this.location_id}`,
    }));
  }

  get hasNoSelectedLocation() {
    return !this.selectedLocationId;
  }

  get roomFilterOptions() {
    return this.rooms
      .filter((room) => this.roomMatchesLocationFilter(room))
      .map((room) => ({
        id: room.id,
        name: room.name,
        isSelected: `${room.id}` === `${this.room_id}`,
      }));
  }

  get roomDisplayOptions() {
    const rooms = this.room_id
      ? this.rooms.filter((room) => `${room.id}` === `${this.room_id}`)
      : this.rooms.filter((room) => this.roomMatchesLocationFilter(room));

    return rooms.map((room) => ({
      ...room,
      detailUrl: roomUrl(room),
      locationUrl: locationUrl(room.location_record),
    }));
  }

  get categories() {
    return this.model?.categories || [];
  }

  get tags() {
    return this.model?.tags || [];
  }

  get availableTags() {
    return this.model?.availableTags || this.tags;
  }

  get hasTags() {
    return this.tags.length > 0;
  }

  get hasAvailableTags() {
    return this.availableTags.length > 0;
  }

  get tagFilterDisabled() {
    return !this.hasTags;
  }

  get tagFilterPlaceholder() {
    return this.hasTags ? "All tags" : "No tags available";
  }

  get organizers() {
    return this.model?.organizers || [];
  }

  get hasOrganizers() {
    return this.organizers.length > 0;
  }

  get categoryFilterOptions() {
    return this.categories.map((category) => ({
      id: category.id,
      name: category.name,
      isSelected: `${category.id}` === `${this.category_id}`,
    }));
  }

  get categoryOptions() {
    return this.categories.map((category) => ({
      id: category.id,
      name: category.name,
      isSelected: `${category.id}` === `${this.selectedCategoryId}`,
    }));
  }

  get tagFilterOptions() {
    const selectedTagIds = this.selectedTagIds;

    return this.tags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      isSelected: selectedTagIds.includes(`${tag.id}`),
    }));
  }

  get selectedTagFilterLabel() {
    const selectedTags = this.tagFilterOptions.filter((tag) => tag.isSelected);

    if (selectedTags.length === 0) {
      return this.tagFilterPlaceholder;
    }

    if (selectedTags.length <= 2) {
      return selectedTags.map((tag) => tag.name).join(", ");
    }

    return `${selectedTags.length} tags selected`;
  }

  get selectedTagIds() {
    return [
      ...tagFilterIds(this.tag_ids),
      ...tagFilterIds(this.tag_id),
    ].filter((value, index, values) => values.indexOf(value) === index);
  }

  get selectedCreateTagIds() {
    return tagFilterIds(this.createTagIds);
  }

  get roleUserSuggestionOptions() {
    return this.roleUserOptions;
  }

  get showCreateAdvancedPeopleFields() {
    return Boolean(
      this.showCreatePeopleDetails ||
        this.coHostNames.trim() ||
        this.speakerNames.trim() ||
        this.withNames.trim()
    );
  }

  get createPeopleToggleLabel() {
    return this.showCreateAdvancedPeopleFields
      ? "Hide additional people"
      : "Add co-hosts, speakers, or partners";
  }

  get pendingCreateTagNames() {
    return tagNames(this.tagNamesCsv);
  }

  get createTagOptions() {
    const selectedTagIds = this.selectedCreateTagIds;

    return this.availableTags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      isSelected: selectedTagIds.includes(`${tag.id}`),
    }));
  }

  get selectedCreateTags() {
    const selectedTagIds = this.selectedCreateTagIds;

    return this.availableTags
      .filter((tag) => selectedTagIds.includes(`${tag.id}`))
      .map((tag) => ({
        id: tag.id,
        name: tag.name,
      }));
  }

  get hasCreateTagSelections() {
    return this.selectedCreateTags.length > 0 || this.pendingCreateTagNames.length > 0;
  }

  get createTagSummaryLabel() {
    const count = this.selectedCreateTags.length + this.pendingCreateTagNames.length;

    if (count === 0) {
      return "Choose tags";
    }

    if (count <= 2) {
      return [
        ...this.selectedCreateTags.map((tag) => tag.name),
        ...this.pendingCreateTagNames,
      ].join(", ");
    }

    return `${count} tags selected`;
  }

  get normalizedNewTagName() {
    return normalizeTagName(this.newTagName);
  }

  get cannotAddCreateTag() {
    return !this.normalizedNewTagName;
  }

  get eventImagePreviewUrl() {
    return uploadPreviewUrl(this.imageUpload);
  }

  get pendingAiImagePreviewUrl() {
    return uploadPreviewUrl(this.pendingAiImageUpload);
  }

  get hasEventImageUpload() {
    return Boolean(this.eventImagePreviewUrl);
  }

  get hasPendingAiImageUpload() {
    return Boolean(this.pendingAiImagePreviewUrl);
  }

  get canUseAiImageGeneration() {
    return this.model?.ai_image_generation_available === true;
  }

  get cannotComposeAiImagePrompt() {
    return this.isComposingAiImagePrompt || this.isGeneratingAiImage;
  }

  get cannotGenerateAiImage() {
    return (
      this.isComposingAiImagePrompt ||
      this.isGeneratingAiImage ||
      this.aiImagePrompt.trim().length === 0
    );
  }

  get aiImagePromptToggleLabel() {
    if (this.isComposingAiImagePrompt) {
      return "Preparing prompt...";
    }

    return this.aiImagePrompt ? "Edit prompt" : "Generate with Discourse AI";
  }

  get roomImagePreviewUrl() {
    return uploadPreviewUrl(this.roomImageUpload);
  }

  get hasRoomImageUpload() {
    return Boolean(this.roomImagePreviewUrl);
  }

  categoryMatchesSelectedFilter(categoryId) {
    if (!this.category_id) {
      return true;
    }

    if (`${categoryId}` === `${this.category_id}`) {
      return true;
    }

    let category = this.categories.find((candidate) => `${candidate.id}` === `${categoryId}`);
    const seenCategoryIds = new Set();

    while (category?.parent_category_id && !seenCategoryIds.has(`${category.id}`)) {
      seenCategoryIds.add(`${category.id}`);

      if (`${category.parent_category_id}` === `${this.category_id}`) {
        return true;
      }

      category = this.categories.find(
        (candidate) => `${candidate.id}` === `${category.parent_category_id}`
      );
    }

    return false;
  }

  get organizerFilterOptions() {
    return this.organizers.map((organizer) => ({
      id: organizer.id,
      name: organizer.name || organizer.username,
      username: organizer.username,
      label: organizer.name ? `${organizer.name} (@${organizer.username})` : `@${organizer.username}`,
      isSelected: `${organizer.id}` === `${this.owner_id}`,
    }));
  }

  get timeOptions() {
    const options = [];

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const value = `${pad(hour)}:${pad(minute)}`;
        options.push({ value, label: timeLabel(value) });
      }
    }

    return options;
  }

  get startTimeOptions() {
    return this.timeOptions.map((option) => ({
      ...option,
      isSelected: option.value === this.startTime,
    }));
  }

  get timezoneOptions() {
    return timezoneOptions(this.timezone, this.model?.defaults?.timezone);
  }

  get endTimeOptions() {
    return this.timeOptions.map((option) => ({
      ...option,
      isSelected: option.value === this.endTime,
    }));
  }

  get recurrenceOptions() {
    return ["none", "daily", "weekly", "monthly", "yearly"].map((rule) => ({
      rule,
      label: recurrenceLabel(rule),
      isSelected: this.recurrenceRule === rule,
    }));
  }

  get isRecurring() {
    return this.recurrenceRule !== "none";
  }

  get recurrenceIntervalLabel() {
    const interval = normalizedInterval(this.recurrenceInterval);
    return `Every ${interval} ${recurrenceUnit(this.recurrenceRule, interval)}`;
  }

  get isNoRecurrence() {
    return this.recurrenceRule === "none";
  }

  get isDailyRecurrence() {
    return this.recurrenceRule === "daily";
  }

  get isWeeklyRecurrence() {
    return this.recurrenceRule === "weekly";
  }

  get selectedRecurrenceByday() {
    return this.recurrenceByday
      .split(",")
      .map((day) => day.trim())
      .filter(Boolean);
  }

  get recurrenceWeekdayOptions() {
    const selected = new Set(this.selectedRecurrenceByday);

    return RECURRENCE_WEEKDAYS.map((weekday) => ({
      ...weekday,
      isSelected: selected.has(weekday.code),
    }));
  }

  get customWeeklyRrule() {
    if (!this.isWeeklyRecurrence) {
      return "";
    }

    const byday = this.selectedRecurrenceByday;
    if (byday.length === 0) {
      return "";
    }

    const defaultDay = weekdayCodeForDate(this.eventDate);
    if (byday.length === 1 && byday[0] === defaultDay) {
      return "";
    }

    return weeklyRrule({
      byday,
      interval: this.recurrenceInterval,
      until: this.recurrenceUntil,
    });
  }

  get isMonthlyRecurrence() {
    return this.recurrenceRule === "monthly";
  }

  get isYearlyRecurrence() {
    return this.recurrenceRule === "yearly";
  }

  get recurrenceUntilMin() {
    return this.eventDate;
  }

  get quickDates() {
    const today = new Date();
    const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    const weekend = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const daysUntilSaturday = (6 - today.getDay() + 7) % 7 || 7;
    weekend.setDate(today.getDate() + daysUntilSaturday);

    return [
      { label: "Today", date: dateString(today) },
      { label: "Tomorrow", date: dateString(tomorrow) },
      { label: "Saturday", date: dateString(weekend) },
    ];
  }

  get calendarLabel() {
    const date = parseDateString(this.calendarMonth);

    return safeDateFormat(
      date,
      {
        month: "long",
        year: "numeric",
      },
      fallbackMonthYear(date)
    );
  }

  get calendarDays() {
    const selectedDate = this.eventDate;
    const today = dateString(new Date());
    const firstDay = parseDateString(this.calendarMonth);
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = new Date(
      firstDay.getFullYear(),
      firstDay.getMonth() + 1,
      0
    ).getDate();
    const days = [];

    for (let index = 0; index < firstDayOfWeek; index++) {
      days.push({ key: `spacer-${index}`, isSpacer: true });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(firstDay.getFullYear(), firstDay.getMonth(), day);
      const value = dateString(date);
      const classes = ["discourse-events-calendar__calendar-day"];

      if (value === selectedDate) {
        classes.push("discourse-events-calendar__calendar-day--selected");
      }

      if (value === today) {
        classes.push("discourse-events-calendar__calendar-day--today");
      }

      days.push({
        key: value,
        date: value,
        day,
        isSpacer: false,
        isSelected: value === selectedDate,
        className: classes.join(" "),
      });
    }

    return days;
  }

  get reservableRooms() {
    return this.rooms
      .filter((room) => room.can_reserve !== false)
      .filter((room) => this.roomMatchesSelectedCategory(room))
      .filter((room) => this.roomMatchesSelectedLocation(room));
  }

  get reservableRoomOptions() {
    return this.reservableRooms.map((room) => ({
      ...room,
      isSelected: `${room.id}` === `${this.selectedRoomId}`,
    }));
  }

  get hasNoSelectedRoom() {
    return !this.selectedRoomId;
  }

  get hasRooms() {
    return this.reservableRooms.length > 0;
  }

  get canManageRooms() {
    return this.model?.can_manage_rooms === true;
  }

  get canCreateEvents() {
    return this.model?.can_create_event === true;
  }

  get placesEnabled() {
    return this.model?.places_enabled === true || this.siteSettings?.discourse_places_enabled === true;
  }

  get rsvpGloballyEnabled() {
    return this.siteSettings?.discourse_events_calendar_rsvp_globally_enabled !== false;
  }

  get hasPlaceOptions() {
    return this.placeOptions.length > 0;
  }

  get subscribeUrl() {
    if (this.location_id) {
      return `/calendar/locations/${encodeURIComponent(this.location_id)}/feed.ics`;
    }

    if (this.category_id) {
      return `/calendar/categories/${encodeURIComponent(this.category_id)}/feed.ics`;
    }

    return "/calendar/feed.ics";
  }

  get subscribeTitle() {
    if (this.location_id) {
      return "Subscribe to events for this location";
    }

    if (this.category_id) {
      return "Subscribe to events in this category";
    }

    return "Subscribe to the public events calendar";
  }

  get canUsePersonalFilters() {
    return this.model?.can_use_personal_filters === true || Boolean(this.currentUser);
  }

  get currentSubscriptionFilter() {
    const tagIds = this.selectedTagIds
      .map((id) => parseInt(id, 10))
      .filter((id) => id > 0);
    const tagNames = this.selectedTagIds
      .map((id) => this.availableTags.find((tag) => `${tag.id}` === `${id}`)?.name)
      .filter(Boolean);
    const filter = {
      category_ids: idFilterValues(this.category_id),
      tag_ids: tagIds,
      tag_names: tagNames,
      location_ids: idFilterValues(this.location_id),
      room_ids: idFilterValues(this.room_id),
      owner_ids: idFilterValues(this.owner_id),
      search: this.q || "",
      start_after: this.activeRangeStart,
      start_before: this.activeRangeEnd,
      timezone: browserTimeZone(),
      scope: this.activeScope,
      rsvp_status: this.activeRsvpStatus,
      visibility: this.activeEventVisibility,
      include_hidden: false,
    };

    Object.keys(filter).forEach((key) => {
      const value = filter[key];
      if (Array.isArray(value) ? value.length === 0 : value === "" || value === null || value === undefined) {
        delete filter[key];
      }
    });

    return filter;
  }

  get eventDescriptionUploadIcon() {
    return uploadIcon(this.currentUser?.staff, this.siteSettings);
  }

  get activeFilter() {
    return this.filter || this.model?.filter || "upcoming";
  }

  get activeScope() {
    return normalizeScope(this.scope || this.model?.scope || "all");
  }

  get myRsvpsFilterButtonClass() {
    return this.personalFilterButtonClass(this.isMyRsvpsScope);
  }

  get myEventsFilterButtonClass() {
    return this.personalFilterButtonClass(this.isOrganizingScope);
  }

  get myRsvpsAriaPressed() {
    return this.isMyRsvpsScope ? "true" : "false";
  }

  get myEventsAriaPressed() {
    return this.isOrganizingScope ? "true" : "false";
  }

  get activeRsvpStatus() {
    return normalizeRsvpStatus(this.rsvp_status ?? this.model?.rsvp_status ?? "");
  }

  get activeEventVisibility() {
    return normalizeEventVisibility(this.event_visibility ?? this.model?.event_visibility ?? "");
  }

  get timeGridScrollShellClass() {
    const classes = ["discourse-events-calendar__time-grid-scroll-shell"];

    if (this.timeGridCanScrollLeft) {
      classes.push("discourse-events-calendar__time-grid-scroll-shell--can-scroll-left");
    }

    if (this.timeGridCanScrollRight) {
      classes.push("discourse-events-calendar__time-grid-scroll-shell--can-scroll-right");
    }

    return classes.join(" ");
  }

  get showCustomBrowserTools() {
    return true;
  }

  get canUsePersonalFilters() {
    return this.model?.can_use_personal_filters === true;
  }

  get subscriptionManagementUrl() {
    const username = this.currentUser?.username;

    if (!username) {
      return "";
    }

    return `/u/${encodeURIComponent(username)}/preferences/events-calendar-subscriptions`;
  }

  get subscribeViewButtonClasses() {
    const classes = ["btn", "btn-default"];

    if (this.subscribe === "1") {
      classes.push("discourse-events-calendar__subscribe-view--highlighted");
    }

    return classes.join(" ");
  }

  get canUseExternalCalendars() {
    return Boolean(this.currentUser);
  }

  get hasExternalCalendarConnections() {
    return this.externalCalendarConnections.length > 0;
  }

  get edgeosRsvpSyncEnabled() {
    return (
      this.siteSettings?.discourse_events_calendar_edgeos_rsvp_sync_enabled === true
    );
  }

  get hasEdgeosRsvpConnection() {
    return this.externalCalendarConnections.some(
      (connection) => connection.provider === "edgeos"
    );
  }

  get shouldShowEdgeosRsvpHint() {
    return (
      this.isMyRsvpsScope &&
      this.edgeosRsvpSyncEnabled &&
      this.canUseExternalCalendars &&
      this.edgeosConnectionsChecked &&
      !this.hasEdgeosRsvpConnection &&
      !this.edgeosRsvpHintDismissed
    );
  }

  async maybeCheckEdgeosConnections() {
    if (
      !this.edgeosRsvpSyncEnabled ||
      !this.canUseExternalCalendars ||
      this.edgeosConnectionsChecked ||
      this.isLoadingExternalCalendars
    ) {
      return;
    }

    await this.loadExternalCalendarConnections();
    this.edgeosConnectionsChecked = true;
  }

  @action
  dismissEdgeosRsvpHint() {
    this.edgeosRsvpHintDismissed = true;
  }

  get canConnectEdgeosFromPanel() {
    return this.edgeosRsvpSyncEnabled && !this.hasEdgeosRsvpConnection;
  }

  @action
  hideExternalCalendarPanel() {
    this.closeExternalCalendarPanel();
  }

  @action
  toggleEdgeosConnectForm() {
    this.showEdgeosConnectForm = !this.showEdgeosConnectForm;
    this.externalCalendarNotice = "";
    this.externalCalendarError = "";
  }

  @action
  setEdgeosApiKeyInput(event) {
    this.edgeosApiKeyInput = event.target.value;
  }

  @action
  async connectEdgeos(event) {
    event?.preventDefault?.();

    const apiKey = this.edgeosApiKeyInput.trim();
    if (!apiKey) {
      this.externalCalendarError = "Paste your EdgeOS API key first.";
      return;
    }

    this.isConnectingEdgeos = true;
    this.externalCalendarNotice = "";
    this.externalCalendarError = "";

    try {
      const response = await ajax("/calendar/external/connections.json", {
        type: "POST",
        data: { provider: "edgeos", api_key: apiKey },
      });
      const nextConnection = externalCalendarConnectionView(response.connection);
      this.externalCalendarConnections = [
        ...this.externalCalendarConnections.filter(
          (existing) => `${existing.id}` !== `${nextConnection.id}`
        ),
        nextConnection,
      ];
      this.edgeosApiKeyInput = "";
      this.showEdgeosConnectForm = false;
      this.edgeosConnectionsChecked = true;
      this.externalCalendarNotice = "EdgeOS connected. Your RSVPs are syncing now.";
    } catch (error) {
      this.externalCalendarError =
        error?.jqXHR?.responseJSON?.errors?.[0] ||
        "EdgeOS could not be connected. Check the API key.";
    } finally {
      this.isConnectingEdgeos = false;
    }
  }

  get activeBrowserMonth() {
    return (
      this.browserMonth ||
      monthString(parseDateString(this.start || this.model?.start || dateString(new Date())))
    );
  }

  get browserFocusDate() {
    if (this.selectedBrowserDate) {
      return this.selectedBrowserDate;
    }

    const today = dateString(new Date());
    const range = monthRange(this.activeBrowserMonth);

    if (!dateIsBefore(today, range.start) && dateIsBefore(today, range.end)) {
      return today;
    }

    return this.activeRangeStart;
  }

  get browserMonthLabel() {
    const date = parseDateString(this.activeBrowserMonth);

    return safeDateFormat(
      date,
      {
        month: "long",
        year: "numeric",
      },
      fallbackMonthYear(date)
    );
  }

  get browserPeriodLabel() {
    if (this.isWeekView) {
      return formatRangeLabel(this.activeRangeStart, this.activeRangeEnd);
    }

    if (this.isDayView) {
      return fallbackMonthYear(parseDateString(this.activeRangeStart));
    }

    if (this.isVenueView) {
      return formatDateLabel(this.activeRangeStart);
    }

    return this.browserMonthLabel;
  }

  get browserPeriodPhrase() {
    if (this.isWeekView) {
      return `during ${this.browserPeriodLabel}`;
    }

    if (this.isDayView || this.isVenueView) {
      return `on ${this.browserPeriodLabel}`;
    }

    return `in ${this.browserMonthLabel}`;
  }

  get previousPeriodLabel() {
    if (this.isWeekView) {
      return "Previous week";
    }

    if (this.isDayView || this.isVenueView) {
      return "Previous day";
    }

    return "Previous month";
  }

  get nextPeriodLabel() {
    if (this.isWeekView) {
      return "Next week";
    }

    if (this.isDayView || this.isVenueView) {
      return "Next day";
    }

    return "Next month";
  }

  get activeRangeStart() {
    const start = this.start || this.model?.start || dateString(new Date());

    if (this.isWeekView) {
      return weekRange(start).start;
    }

    if (this.isDayView || this.isVenueView) {
      return dayRange(start).start;
    }

    if (this.isMonthView || this.isAgendaView) {
      return monthRange(start).start;
    }

    return start;
  }

  get activeRangeEnd() {
    const start = this.start || this.model?.start || dateString(new Date());

    if (this.isWeekView) {
      return weekRange(start).end;
    }

    if (this.isDayView || this.isVenueView) {
      return dayRange(start).end;
    }

    if (this.isMonthView || this.isAgendaView) {
      return monthRange(start).end;
    }

    return this.end || this.model?.end || nextDayString(this.activeRangeStart);
  }

  get eventsStateSignature() {
    return [
      this.activeRangeStart,
      this.activeRangeEnd,
      this.activeFilter,
      this.q,
      this.room_id,
      this.location_id,
      this.category_id,
      this.place_id,
      this.tag_id,
      this.tag_ids,
      this.owner_id,
      this.activeEventVisibility,
      this.activeScope,
      this.activeRsvpStatus,
    ].join("::");
  }

  get nonSearchEventsStateSignature() {
    return [
      this.activeRangeStart,
      this.activeRangeEnd,
      this.activeFilter,
      this.room_id,
      this.location_id,
      this.category_id,
      this.place_id,
      this.tag_id,
      this.tag_ids,
      this.owner_id,
      this.activeEventVisibility,
      this.activeScope,
      this.activeRsvpStatus,
    ].join("::");
  }

  get timeGridScrollFocusSignature() {
    return [
      this.view,
      this.activeRangeStart,
      this.activeRangeEnd,
      this.timeGridStartHour,
      this.timeGridEndHour,
    ].join("::");
  }

  get browserMonthDays() {
    const today = dateString(new Date());
    const firstDay = parseDateString(this.activeBrowserMonth);
    const range = monthRange(this.activeBrowserMonth);
    const eventsByDate = this.eventsByDateForRange(range.start, range.end);
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = new Date(
      firstDay.getFullYear(),
      firstDay.getMonth() + 1,
      0
    ).getDate();
    const days = [];

    for (let index = 0; index < firstDayOfWeek; index++) {
      days.push({ key: `browser-spacer-${index}`, isSpacer: true });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(firstDay.getFullYear(), firstDay.getMonth(), day);
      const value = dateString(date);
      const events = eventsByDate.get(value) || [];
      const serverTotalEvents = Number(
        this.model?.month_overflow_counts?.[value] ?? events.length
      );
      const totalEvents = this.hasClientEventFilters ? events.length : serverTotalEvents;
      const classes = ["discourse-events-calendar__month-day"];

      if (value === today) {
        classes.push("discourse-events-calendar__month-day--today");
      }

      if (value === this.selectedBrowserDate) {
        classes.push("discourse-events-calendar__month-day--selected");
      }

      if (events.length > 0) {
        classes.push("discourse-events-calendar__month-day--has-events");
      }

      const isMoreOpen = value === this.openMonthMoreDate;
      if (isMoreOpen) {
        classes.push("discourse-events-calendar__month-day--more-open");
      }

      if (this.selectedPreviewEvent && value === this.eventPreviewDate) {
        classes.push("discourse-events-calendar__month-day--previewed");
      }

      days.push({
        key: value,
        date: value,
        day,
        events,
        visibleEvents: events.slice(0, 4),
        moreEvents: isMoreOpen ? events : [],
        moreCount: Math.max(totalEvents - Math.min(events.length, 4), 0),
        isMoreLoading: value === this.loadingMonthMoreDate,
        isMoreOpen,
        hasEvents: events.length > 0,
        isSelected: value === this.selectedBrowserDate,
        className: classes.join(" "),
        morePopoverClass: [
          "discourse-events-calendar__month-more-popover",
          date.getDay() >= 4
            ? "discourse-events-calendar__month-more-popover--align-end"
            : "",
        ]
          .filter(Boolean)
          .join(" "),
      });
    }

    return days;
  }

  get weekViewDays() {
    return this.calendarRangeDays(7);
  }

  get dayViewDays() {
    return this.calendarRangeDays(1);
  }

  get timeGridStartHour() {
    return 0;
  }

  get timeGridEndHour() {
    return 24;
  }

  get timeGridHours() {
    const hours = [];

    for (let hour = this.timeGridStartHour; hour < this.timeGridEndHour; hour++) {
      const date = new Date(2000, 0, 1, hour, 0, 0, 0);
      hours.push({
        hour,
        label: compactTime(date),
      });
    }

    return hours;
  }

  get weekTimeGridDays() {
    return this.calendarRangeTimeGridDays(7);
  }

  get dayTimeGridDays() {
    return this.calendarRangeTimeGridDays(1);
  }

  get venueDateStripDays() {
    const range = weekRange(this.activeRangeStart);
    const selectedDate = this.activeRangeStart;

    return this.calendarRangeDays(7, range.start).map((day) => ({
      ...day,
      isSelected: day.date === selectedDate,
      className: [
        "discourse-events-calendar__venue-date",
        day.date === selectedDate ? "discourse-events-calendar__venue-date--selected" : "",
        day.hasEvents ? "discourse-events-calendar__venue-date--has-events" : "",
      ]
        .filter(Boolean)
        .join(" "),
    }));
  }

  get dayDateStripDays() {
    const selectedDate = this.activeRangeStart;
    const stripStart = dateString(addDays(selectedDate, -3));

    return this.calendarRangeDays(7, stripStart).map((day) => ({
      ...day,
      isSelected: day.date === selectedDate,
      className: [
        "discourse-events-calendar__day-date",
        day.date === selectedDate ? "discourse-events-calendar__day-date--selected" : "",
        day.isToday ? "discourse-events-calendar__day-date--today" : "",
      ]
        .filter(Boolean)
        .join(" "),
    }));
  }

  calendarRangeDays(dayCount, startDate = this.activeRangeStart) {
    const today = dateString(new Date());
    const days = [];
    const rangeEnd = dateString(addDays(startDate, dayCount));
    const eventsByDate = this.eventsByDateForRange(startDate, rangeEnd);

    for (let index = 0; index < dayCount; index++) {
      const date = addDays(startDate, index);
      const value = dateString(date);
      const events = eventsByDate.get(value) || [];
      const classes = ["discourse-events-calendar__range-day"];

      if (value === today) {
        classes.push("discourse-events-calendar__range-day--today");
      }

      days.push({
        key: value,
        date: value,
        dayName: safeDateFormat(
          date,
          { weekday: "short" },
          ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][date.getDay()]
        ),
        dayNumber: date.getDate(),
        label: formatDateLabel(value),
        events,
        hasEvents: events.length > 0,
        isToday: value === today,
        className: classes.join(" "),
      });
    }

    return days;
  }

  calendarRangeTimeGridDays(dayCount, startDate = this.activeRangeStart) {
    const gridStartMinutes = this.timeGridStartHour * 60;
    const gridEndMinutes = this.timeGridEndHour * 60;

    return this.calendarRangeDays(dayCount, startDate).map((day) => {
      const createSelection =
        this.timeGridDraft?.date === day.date
          ? timeGridDraftItem(this.timeGridDraft, gridStartMinutes, gridEndMinutes)
          : null;
      const allDayEvents = day.events.filter((event) =>
        eventIsAllDayForDate(event, day.date)
      );
      const timedEvents = day.events.filter(
        (event) => !eventIsAllDayForDate(event, day.date)
      );
      const events = timeGridEventItems(
        timedEvents,
        day.date,
        gridStartMinutes,
        gridEndMinutes
      );
      const classes = ["discourse-events-calendar__time-grid-day"];

      if (day.isToday) {
        classes.push("discourse-events-calendar__time-grid-day--today");
      }

      if (this.canCreateEvents) {
        classes.push("discourse-events-calendar__time-grid-day--can-create");
      }

      if (createSelection) {
        classes.push("discourse-events-calendar__time-grid-day--selecting");
      }

      return {
        ...day,
        allDayEvents,
        hasAllDayEvents: allDayEvents.length > 0,
        events,
        createSelection,
        hasEvents: events.length > 0 || allDayEvents.length > 0,
        className: classes.join(" "),
      };
    });
  }

  get isMonthView() {
    return this.view === "month";
  }

  get monthViewTabClass() {
    return viewTabClass(this.isMonthView);
  }

  get isAgendaView() {
    return this.view === "agenda";
  }

  get agendaViewTabClass() {
    return viewTabClass(this.isAgendaView);
  }

  get isWeekView() {
    return this.view === "week";
  }

  get weekViewTabClass() {
    return viewTabClass(this.isWeekView);
  }

  get isDayView() {
    return this.view === "day";
  }

  get dayViewTabClass() {
    return viewTabClass(this.isDayView);
  }

  get isVenueView() {
    return this.view === "venue";
  }

  get venueViewTabClass() {
    return viewTabClass(this.isVenueView);
  }

  get venueDayEvents() {
    return this.events.filter((event) => eventOverlapsDate(event, this.activeRangeStart));
  }

  get venueColumns() {
    const columns = this.roomDisplayOptions.map((room) => ({
      key: `room-${room.id}`,
      title: room.name,
      location: room.location,
      roomId: room.id,
      imageUrl: room.image_url || room.location_record?.image_url || "",
      headerClass: [
        "discourse-events-calendar__venue-column-header",
        room.image_url || room.location_record?.image_url
          ? "discourse-events-calendar__venue-column-header--image"
          : "",
      ]
        .filter(Boolean)
        .join(" "),
      canQuickAdd: true,
      detailUrl: room.detailUrl,
      locationUrl: room.locationUrl,
      events: this.venueDayEvents.filter((event) => `${event.room?.id}` === `${room.id}`),
    }));
    const unassignedEvents = this.venueDayEvents.filter((event) => !event.room?.id);

    if (unassignedEvents.length > 0 || columns.length === 0) {
      columns.push({
        key: "unassigned",
        title: "No room",
        location: "",
        roomId: "",
        imageUrl: "",
        headerClass: "discourse-events-calendar__venue-column-header",
        canQuickAdd: false,
        detailUrl: "",
        locationUrl: "",
        events: unassignedEvents,
      });
    }

    return columns;
  }

  get hasSelectedBrowserDate() {
    return this.selectedBrowserDate.length > 0;
  }

  get hasSelectedPreviewRoom() {
    return Boolean(this.selectedPreviewEvent?.room?.name);
  }

  get selectedPreviewTopicUrl() {
    return this.selectedPreviewEvent?.topic_id
      ? `/t/-/${this.selectedPreviewEvent.topic_id}`
      : "";
  }

  get isSavingSelectedPreviewRsvp() {
    return (
      this.isSavingPreviewRsvp &&
      this.previewRsvpSavingKey === previewEventKey(this.selectedPreviewEvent)
    );
  }

  get eventPreviewClass() {
    return [
      "discourse-events-calendar__event-popover",
      "discourse-events-calendar__event-preview-popover",
      "discourse-events-calendar__event-preview-popover--custom",
      this.eventPreviewSource
        ? `discourse-events-calendar__event-preview-popover--${this.eventPreviewSource}`
      : "",
    ].join(" ");
  }

  get previewNavigationEvents() {
    return sortEvents(
      this.events.filter((event) =>
        eventOverlapsRange(event, this.activeRangeStart, this.activeRangeEnd)
      ),
      this.activeFilter
    );
  }

  get selectedPreviewEventIndex() {
    const selectedKey = previewEventKey(this.selectedPreviewEvent);

    return this.previewNavigationEvents.findIndex(
      (event) => previewEventKey(event) === selectedKey
    );
  }

  get showEventPreviewNavigation() {
    return this.previewNavigationEvents.length > 1 && this.selectedPreviewEventIndex >= 0;
  }

  get eventPreviewPositionLabel() {
    if (!this.showEventPreviewNavigation) {
      return "";
    }

    return `${this.selectedPreviewEventIndex + 1} of ${this.previewNavigationEvents.length}`;
  }

  get selectedBrowserDateLabel() {
    return this.selectedBrowserDate ? formatDateLabel(this.selectedBrowserDate) : "";
  }

  get activeBrowserFilterCount() {
    return [
      this.activeFilter !== "upcoming" ? this.activeFilter : "",
      this.q,
      this.room_id,
      this.location_id,
      this.category_id,
      this.selectedTagIds.length > 0 ? this.selectedTagIds.join(",") : "",
      this.owner_id,
      this.activeEventVisibility,
      this.activeRsvpStatus,
      this.activeScope !== "all" ? this.activeScope : "",
      this.selectedBrowserDate,
    ].filter(Boolean).length;
  }

  get hasActiveBrowserFilters() {
    return this.activeBrowserFilterCount > 0;
  }

  get browserFiltersClass() {
    return [
      "discourse-events-calendar__browser-filters",
      this.isMobileFiltersExpanded
        ? "discourse-events-calendar__browser-filters--mobile-expanded"
        : "discourse-events-calendar__browser-filters--mobile-collapsed",
    ].join(" ");
  }

  get mobileFiltersToggleLabel() {
    if (this.isMobileFiltersExpanded) {
      return "Hide filters";
    }

    return this.activeBrowserFilterCount > 0
      ? `Filters (${this.activeBrowserFilterCount})`
      : "Filters";
  }

  get showCollapsedMobileFilterClear() {
    return this.hasActiveBrowserFilters && !this.isMobileFiltersExpanded;
  }

  get activeAdvancedFilterCount() {
    return [
      this.activeRsvpStatus,
      this.location_id,
      this.room_id,
      this.owner_id,
      this.activeEventVisibility,
    ].filter(Boolean).length;
  }

  get advancedFiltersLabel() {
    if (this.showAdvancedFilters) {
      return "Hide filters";
    }

    return this.activeAdvancedFilterCount > 0
      ? `More filters (${this.activeAdvancedFilterCount})`
      : "More filters";
  }

  get showCreatePanel() {
    return Boolean(this.isCreateFormExpanded || this.createNotice || this.createError);
  }

  get showHeaderCreateButton() {
    return this.canCreateEvents;
  }

  get createFormToggleLabel() {
    return "New event";
  }

  get showCreateChooser() {
    return false;
  }

  get showCreateMenu() {
    return this.showCreateChooser && this.isCreateMenuOpen;
  }

  get hasNoRoomFilter() {
    return !this.room_id;
  }

  get hasNoLocationFilter() {
    return !this.location_id;
  }

  get hasNoCategoryFilter() {
    return !this.category_id;
  }

  get hasNoTagFilter() {
    return this.selectedTagIds.length === 0;
  }

  get hasNoOwnerFilter() {
    return !this.owner_id;
  }

  get hasNoCategorySelected() {
    return !this.selectedCategoryId;
  }

  get isPublicVisibility() {
    return this.visibility === "public";
  }

  get isUnlistedVisibility() {
    return this.visibility === "unlisted";
  }

  get isStaffVisibility() {
    return this.visibility === "staff";
  }

  get isPrivateVisibility() {
    return this.visibility === "private";
  }

  get showInviteesSection() {
    return this.isPrivateVisibility;
  }

  get hasCreateInvitees() {
    return Boolean(this.inviteUsernames.trim() || this.inviteGroupNames.trim());
  }

  get canUseChatThread() {
    return this.model?.defaults?.chat_available === true;
  }

  get isNoConversation() {
    return this.conversationMode === "none";
  }

  get isDiscussionConversation() {
    return this.conversationMode === "discussion";
  }

  get isChatConversation() {
    return this.conversationMode === "chat";
  }

  get isNativeRsvpMode() {
    return this.rsvpMode !== "external";
  }

  get isExternalRsvpMode() {
    return this.rsvpMode === "external";
  }

  get endDate() {
    return this.endsNextDay ? nextDayString(this.eventDate) : this.eventDate;
  }

  get endDateLabel() {
    const date = parseDateString(this.endDate);

    return safeDateFormat(
      date,
      {
        month: "short",
        day: "numeric",
      },
      fallbackDate(date, false)
    );
  }

  get isUpcomingFilter() {
    return this.activeFilter === "upcoming";
  }

  get isPastFilter() {
    return this.activeFilter === "past";
  }

  get isAllFilter() {
    return this.activeFilter === "all";
  }

  get isAllScope() {
    return this.activeScope === "all";
  }

  get isMyRsvpsScope() {
    return this.activeScope === "my_rsvps";
  }

  get isOrganizingScope() {
    return this.activeScope === "organizing";
  }

  get rsvpStatusOptions() {
    return [
      { value: "", label: "Any RSVP" },
      { value: "going", label: "Going" },
      { value: "interested", label: "Interested" },
      { value: "waitlisted", label: "Waitlisted" },
      { value: "not_going", label: "Not going" },
    ].map((option) => ({
      ...option,
      isSelected: option.value === this.activeRsvpStatus,
    }));
  }

  get hasNoRsvpStatusFilter() {
    return !this.activeRsvpStatus;
  }

  get eventVisibilityOptions() {
    return [
      { value: "", label: "Any visibility" },
      { value: "public_visibility", label: "Public" },
      { value: "staff_visibility", label: "Staff only" },
      { value: "private_visibility", label: "Private invite-only" },
    ].map((option) => ({
      ...option,
      isSelected: option.value === this.activeEventVisibility,
    }));
  }

  get hasNoEventVisibilityFilter() {
    return !this.activeEventVisibility;
  }

  get eventListTitle() {
    if (this.selectedBrowserDate) {
      return `Events on ${this.selectedBrowserDateLabel}`;
    }

    const suffix = this.browserPeriodPhrase;

    if (this.isPastFilter) {
      return `Past events ${suffix}`;
    }

    if (this.isAllFilter) {
      return `All events ${suffix}`;
    }

    return `Upcoming events ${suffix}`;
  }

  get emptyText() {
    if (this.selectedBrowserDate) {
      return "No events found for this day.";
    }

    if (this.isWeekView) {
      return "No events found for this week.";
    }

    if (this.isDayView || this.isVenueView) {
      return "No events found for this day.";
    }

    if (this.isPastFilter) {
      return this.isMyRsvpsScope ? "No past RSVPs found." : "No past events found.";
    }

    if (this.isAllFilter) {
      return this.isMyRsvpsScope ? "No RSVPs found." : "No events found.";
    }

    if (this.isMyRsvpsScope) {
      return "No upcoming RSVPs found. RSVPs for events that already happened are hidden by the \"Upcoming\" filter.";
    }

    return "No upcoming events found.";
  }

  get shouldOfferAllEventsForMyRsvps() {
    return this.isMyRsvpsScope && !this.isPastFilter && !this.isAllFilter;
  }

  get canCreateRoom() {
    return this.roomName.trim().length > 0 && !this.isSavingRoom;
  }

  get cannotCreateRoom() {
    return !this.canCreateRoom;
  }

  get canUseVenueFinder() {
    return this.canCreateEvents && this.isExistingVenueType && (this.hasRooms || this.hasLocations);
  }

  get isVenueTbdType() {
    return this.venueType === "tbd";
  }

  get isExistingVenueType() {
    return this.venueType === "existing";
  }

  get isCustomVenueType() {
    return this.venueType === "custom";
  }

  get isVirtualVenueType() {
    return this.venueType === "virtual";
  }

  get hasSelectedExistingVenue() {
    return Boolean(this.selectedLocationId || this.selectedRoomId || this.placeId);
  }

  get hasValidVenueDetails() {
    if (this.isVirtualVenueType) {
      return normalizedUrlInput(this.meetingUrl).length > 0;
    }

    if (this.isCustomVenueType) {
      return this.location.trim().length > 0;
    }

    if (this.isExistingVenueType) {
      return this.hasSelectedExistingVenue;
    }

    return true;
  }

  get selectedPlaceSummary() {
    return this.placeId && this.placeQuery ? this.placeQuery : "";
  }

  get venueSearchButtonLabel() {
    return this.showVenueSearch ? "Hide search" : "Search places";
  }

  get hasVenueSuggestions() {
    return this.venueSuggestions.length > 0;
  }

  get hasVenueFinderMissingFields() {
    return this.venueFinderMissingFields.length > 0;
  }

  get venueFinderMissingText() {
    if (!this.hasVenueFinderMissingFields) {
      return "";
    }

    const missingFields = new Set(this.venueFinderMissingFields);

    if (!missingFields.has("starts_at") && missingFields.has("ends_at") && this.startTime && this.endTime) {
      return "Choose an end time after the start time before suggesting venues.";
    }

    const labels = [];

    if (missingFields.has("starts_at")) {
      labels.push("a date");
    }

    if (missingFields.has("ends_at")) {
      if (!this.startTime) {
        labels.push("a start time");
      }

      if (!this.endTime) {
        labels.push("an end time");
      }
    }

    if (labels.length === 0) {
      return "Add the event schedule before suggesting venues.";
    }

    if (labels.length === 1) {
      return `Add ${labels[0]} before suggesting venues.`;
    }

    return `Add ${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]} before suggesting venues.`;
  }

  get venueFinderButtonLabel() {
    return this.isLoadingVenueSuggestions ? "Matching..." : "Suggest venues";
  }

  get venueFinderSummary() {
    const parts = [];

    if (this.eventDate) {
      if (this.allDay) {
        parts.push(`${this.eventDate}, all day`);
      } else if (this.startTime && this.endTime) {
        parts.push(`${this.eventDate}, ${timeLabel(this.startTime)}-${timeLabel(this.endTime)}`);
      } else {
        parts.push(this.eventDate);
      }
    }

    if (this.capacity) {
      parts.push(`${this.capacity} people`);
    }

    return parts.join(" · ");
  }

  get hasValidTimeRange() {
    if (this.allDay) {
      return Boolean(this.eventDate);
    }

    if (!this.eventDate || !this.startTime || !this.endTime) {
      return false;
    }

    return (
      dateWithTime(this.endDate, this.endTime) >
      dateWithTime(this.eventDate, this.startTime)
    );
  }

  get hasValidRecurrenceRange() {
    return (
      !this.isRecurring ||
      (this.recurrenceUntil && !dateIsBefore(this.recurrenceUntil, this.eventDate))
    );
  }

  get hasInvalidRecurrenceRange() {
    return !this.hasValidRecurrenceRange;
  }

  get hasInvalidTimeRange() {
    if (this.allDay) {
      return false;
    }

    return Boolean(this.eventDate && this.startTime && this.endTime && !this.hasValidTimeRange);
  }

  get canSubmitEvent() {
    return (
      this.canCreateEvents &&
      this.title.trim().length > 0 &&
      this.eventDate.length > 0 &&
      this.hasValidTimeRange &&
      this.hasValidRecurrenceRange &&
      this.hasValidVenueDetails &&
      this.hasValidRsvpDetails &&
      !this.roomAvailabilityBlocksSubmit &&
      !this.isSaving
    );
  }

  get hasValidRsvpDetails() {
    return (
      !this.rsvpEnabled ||
      this.rsvpMode !== "external" ||
      normalizedUrlInput(this.externalRsvpUrl).length > 0
    );
  }

  get cannotSubmitEvent() {
    return !this.canSubmitEvent;
  }

  get hasRoomAvailabilityStatus() {
    return Boolean(this.roomAvailabilityStatus);
  }

  get roomAvailabilityBlocksSubmit() {
    return ["checking", "unavailable"].includes(this.roomAvailabilityStatus);
  }

  get roomAvailabilityClass() {
    return `discourse-events-calendar__availability-check discourse-events-calendar__availability-check--${
      this.roomAvailabilityStatus || "idle"
    }`;
  }

  get createConflictRange() {
    return formatConflictRange(this.createConflict);
  }

  get createConflictTitle() {
    return conflictTitle(this.createConflict);
  }

  get createConflictEventPath() {
    return conflictEventPath(this.createConflict);
  }

  get hasCreateConflictEventPath() {
    return Boolean(this.createConflictEventPath);
  }

  get hasCreateConflictAlternatives() {
    return this.createConflictAlternatives.length > 0;
  }

  get roomAvailabilityConflictRange() {
    return formatConflictRange(this.roomAvailabilityConflict);
  }

  get roomAvailabilityConflictTitle() {
    return conflictTitle(this.roomAvailabilityConflict);
  }

  get roomAvailabilityConflictEventPath() {
    return conflictEventPath(this.roomAvailabilityConflict);
  }

  get hasRoomAvailabilityConflictEventPath() {
    return Boolean(this.roomAvailabilityConflictEventPath);
  }

  resetScheduleFields() {
    const schedule = defaultSchedule();

    this.eventDate = schedule.eventDate;
    this.calendarMonth = schedule.calendarMonth;
    this.startTime = schedule.startTime;
    this.endTime = schedule.endTime;
    this.endsNextDay = schedule.endsNextDay;
    this.allDay = false;
  }

  setDefaultEndTime() {
    const end = dateAndTimeAfter(this.eventDate, this.startTime, 60);

    this.endTime = end.time;
    this.endsNextDay = end.date !== this.eventDate;
  }

  openCreateFormForDate(date, options = {}) {
    this.dismissEventPreview();
    this.closeMonthMore();
    this.closeCreateMenu();

    if (options.keepTimeGridDraft !== true) {
      this.timeGridDraft = null;
    }

    this.resetForm();
    this.selectQuickDate(date);
    this.startTime = options.startTime ?? "";
    this.endTime = options.endTime ?? "";
    this.endsNextDay = options.endsNextDay === true;
    this.isCreateFormExpanded = true;
    this.create = "1";
    this.createNotice = "";
    this.createError = "";
    this.createConflict = null;
    this.createConflictAlternatives = [];
    this.scheduleRoomAvailabilityCheck();
  }

  resetForm() {
    this.title = "";
    this.imageUpload = null;
    this.imageUploadId = "";
    this.imageUploadError = "";
    this.isUploadingImage = false;
    this.showAiImagePrompt = false;
    this.aiImagePrompt = "";
    this.pendingAiImageUpload = null;
    this.isComposingAiImagePrompt = false;
    this.isGeneratingAiImage = false;
    this.resetScheduleFields();
    this.venueType = "tbd";
    this.location = "";
    this.meetingUrl = "";
    this.customLocationUrl = "";
    this.placeQuery = "";
    this.placeId = "";
    this.selectedPlaceCategoryIds = [];
    this.placeOptions = [];
    this.isLoadingPlaces = false;
    this.showVenueSearch = false;
    this.closeVenueFinder();
    this.selectedLocationId = "";
    this.selectedRoomId = "";
    this.selectedCategoryId = "";
    this.createTagIds = "";
    this.tagNamesCsv = "";
    this.newTagName = "";
    this.isCreateTagMenuOpen = false;
    this.hostNames = "";
    this.ensureDefaultHostNames();
    this.coHostNames = "";
    this.speakerNames = "";
    this.withNames = "";
    this.showCreatePeopleDetails = false;
    this.roleUserOptions = [];
    this.isLoadingRoleUsers = false;
    this.inviteUsernames = "";
    this.inviteGroupNames = "";
    this.roomLocationApplied = "";
    this.description = "";
    this.capacity = "";
    this.visibility = "public";
    this.timezone = "";
    this.recurrenceRule = "none";
    this.recurrenceInterval = "1";
    this.recurrenceUntil = "";
    this.recurrenceByday = "";
    this.applyEventDefaults();
    this.createError = "";
    this.createConflict = null;
    this.createConflictAlternatives = [];
    this.clearRoomAvailability();
  }

  applyEventDefaults() {
    const defaults = this.model?.defaults || {};
    const defaultConversationMode = defaults.conversation_mode
      ? normalizeConversationMode(defaults.conversation_mode)
      : defaults.create_chat_thread === true || defaults.create_chat === true
        ? "chat"
        : defaults.create_topic === false
          ? "none"
          : "discussion";
    const conversationMode =
      defaultConversationMode === "chat" && !this.canUseChatThread
        ? defaults.create_topic === false
          ? "none"
          : "discussion"
        : defaultConversationMode;

    this.visibility = visibilityModeFromFields(defaults.visibility, defaults.hidden);
    this.timezone = normalizeTimeZone(browserTimeZone(), defaults.timezone);
    this.rsvpEnabled = defaults.rsvp_enabled !== false;
    this.rsvpMode = "native";
    this.externalRsvpUrl = "";
    this.rsvpAllowInterested = true;
    this.conversationMode = conversationMode;
    this.createTopic = conversationMode === "discussion";
    this.createChat = conversationMode === "chat";
    this.selectedCategoryId = defaults.category_id ? `${defaults.category_id}` : "";
  }

  resetRoomForm() {
    this.roomName = "";
    this.roomLocation = "";
    this.roomImageUpload = null;
    this.roomImageUploadId = "";
    this.roomImageUploadError = "";
    this.isUploadingRoomImage = false;
    this.roomCapacity = "";
    this.roomBookingWindowDays = "";
    this.roomBufferMinutes = "";
    this.roomError = "";
  }

  get loadedEventResultCount() {
    return asArray(this.model?.events || this.model).length;
  }

  get hasPendingEventBatches() {
    return this.eventBatchQueue.length > 0;
  }

  get canLoadMoreEventBatches() {
    return (
      this.eventsLoadIsPartial &&
      this.hasPendingEventBatches &&
      !this.eventsLoadCanRetry &&
      !this.isLoadingEvents
    );
  }

  get canLoadAllEventBatches() {
    return this.canLoadMoreEventBatches && this.eventBatchQueue.length > 1;
  }

  get eventsLoadStatusMessage() {
    if (!this.eventsLoadIsPartial) {
      return "";
    }

    if (this.eventsLoadHardLimited) {
      return i18n("discourse_events_calendar.event_loading.partial_hard_limit", {
        count: this.loadedEventResultCount,
      });
    }

    if (this.eventsLoadCanRetry) {
      return i18n("discourse_events_calendar.event_loading.partial_retry", {
        count: this.loadedEventResultCount,
      });
    }

    if (this.isLoadingEvents) {
      return i18n("discourse_events_calendar.event_loading.busy_loading", {
        completed: this.eventsLoadCompletedBatches,
        total: this.eventsLoadTotalBatches,
      });
    }

    return i18n("discourse_events_calendar.event_loading.partial_ready", {
      count: this.loadedEventResultCount,
      completed: this.eventsLoadCompletedBatches,
      total: this.eventsLoadTotalBatches,
    });
  }

  get eventsLoadStatusClass() {
    return [
      "discourse-events-calendar__events-load-status",
      this.eventsLoadHardLimited || this.eventsLoadCanRetry
        ? "discourse-events-calendar__events-load-status--warning"
        : "discourse-events-calendar__events-load-status--progress",
    ].join(" ");
  }

  get eventsLoadStatusRole() {
    return this.eventsLoadHardLimited || this.eventsLoadCanRetry ? "alert" : "status";
  }

  get showGenericEventsLoading() {
    return this.isLoadingEvents && !this.eventsLoadStatusMessage;
  }

  get shouldAutoLoadAllEventBatches() {
    return !this.isAgendaView || this.hasActiveBrowserFilters || Boolean(this.place_id);
  }

  resetEventBatchLoadState() {
    this.eventBatchQueue = [];
    this.eventBatchLoadSignature = "";
    this.eventsLoadIsPartial = false;
    this.eventsLoadHardLimited = false;
    this.eventsLoadCompletedBatches = 0;
    this.eventsLoadTotalBatches = 0;
    this.eventsLoadCanRetry = false;
  }

  eventRequestData(start, end, { compactMonth = this.isMonthView } = {}) {
    const data = {
      start,
      end,
      summary: true,
      compact_month: compactMonth,
      timezone: browserTimeZone(),
      filter: this.activeFilter,
      q: this.q,
      room_id: this.room_id,
      location_id: this.location_id,
      category_id: this.category_id,
      place_id: this.place_id,
      tag_id: this.tag_id,
      tag_ids: this.tag_ids,
      owner_id: this.owner_id,
      event_visibility: this.activeEventVisibility,
      scope: this.activeScope,
      rsvp_status: this.activeRsvpStatus,
    };

    if (start !== this.activeRangeStart || end !== this.activeRangeEnd) {
      data.metadata_start = this.activeRangeStart;
      data.metadata_end = this.activeRangeEnd;
    }

    return data;
  }

  replaceEventResultsForBatching() {
    const model = Array.isArray(this.model) ? {} : this.model || {};
    this.model = {
      ...model,
      events: [],
      organizers: [],
      tags: [],
      availableTags: [],
      available_tags: [],
      month_overflow_counts: {},
    };
    this.resetEventsCaches();
  }

  applyEventListResponse(response, { replace = false } = {}) {
    const model = Array.isArray(this.model) ? {} : this.model || {};
    const currentEvents = replace ? [] : model.events;
    const currentOrganizers = replace ? [] : model.organizers;
    const currentTags = replace ? [] : model.tags;
    const currentAvailableTags = replace ? [] : model.availableTags;
    const currentOverflowCounts = replace ? {} : model.month_overflow_counts || {};

    this.model = {
      ...model,
      ...response,
      events: mergeCalendarEvents(currentEvents, response?.events || response),
      organizers: mergeRecordsById(currentOrganizers, response?.organizers),
      tags: mergeRecordsById(currentTags, response?.tags),
      availableTags: mergeRecordsById(
        currentAvailableTags,
        response?.available_tags || response?.tags
      ),
      month_overflow_counts: {
        ...currentOverflowCounts,
        ...(response?.month_overflow_counts || {}),
      },
      filter: this.activeFilter,
      q: this.q,
      room_id: this.room_id,
      location_id: this.location_id,
      category_id: this.category_id,
      place_id: this.place_id,
      tag_id: this.tag_id,
      tag_ids: this.tag_ids,
      owner_id: this.owner_id,
      event_visibility: this.activeEventVisibility,
      scope: this.activeScope,
      rsvp_status: this.activeRsvpStatus,
      start: this.activeRangeStart,
      end: this.activeRangeEnd,
      eventsLoadError: "",
      eventsLoadTooLarge: false,
    };
    this.resetEventsCaches();
  }

  initializeEventDateBatches(loadSignature) {
    let batches = buildCalendarDateBatches(
      this.activeRangeStart,
      this.activeRangeEnd,
      EVENT_BATCH_DAYS
    );
    const today = dateString(new Date());

    if (this.activeFilter === "upcoming") {
      batches = batches.filter((batch) => batch.end > today);
    } else if (this.activeFilter === "past") {
      batches = batches.filter((batch) => batch.start <= today).reverse();
    }

    this.eventBatchQueue = batches;
    this.eventBatchLoadSignature = loadSignature;
    this.eventsLoadIsPartial = true;
    this.eventsLoadHardLimited = false;
    this.eventsLoadCompletedBatches = 0;
    this.eventsLoadTotalBatches = batches.length;
    this.eventsLoadCanRetry = false;
    this.replaceEventResultsForBatching();
  }

  async continueEventDateBatches({ requestId, loadSignature, maxSuccessfulBatches }) {
    let successfulThisPass = 0;

    while (
      this.eventBatchQueue.length > 0 &&
      successfulThisPass < maxSuccessfulBatches
    ) {
      if (
        requestId !== this.eventsRequestId ||
        loadSignature !== this.eventsStateSignature
      ) {
        return;
      }

      const batch = this.eventBatchQueue.shift();

      try {
        const response = await ajax("/calendar/events.json", {
          data: this.eventRequestData(batch.start, batch.end),
        });

        if (requestId !== this.eventsRequestId) {
          return;
        }

        this.applyEventListResponse(response, {
          replace: this.eventsLoadCompletedBatches === 0,
        });
        this.eventsLoadCompletedBatches += 1;
        successfulThisPass += 1;
      } catch (error) {
        if (requestId !== this.eventsRequestId) {
          return;
        }

        if (isCalendarWorkLimitError(error)) {
          const smallerBatches = splitCalendarDateBatch(batch);
          if (smallerBatches.length > 0) {
            const orderedBatches =
              this.activeFilter === "past" ? smallerBatches.reverse() : smallerBatches;
            this.eventBatchQueue.unshift(...orderedBatches);
            this.eventsLoadTotalBatches += smallerBatches.length - 1;
            continue;
          }

          // A single calendar day is the minimum useful date window. Keep the
          // other days available, but never bypass the server's hard work cap.
          this.eventsLoadHardLimited = true;
          continue;
        }

        // Preserve already-loaded results and the failed batch. A retry resumes
        // at this exact date window instead of restarting the whole month.
        this.eventBatchQueue.unshift(batch);
        this.eventsLoadCanRetry = true;
        break;
      }
    }

    if (
      this.eventBatchQueue.length === 0 &&
      !this.eventsLoadHardLimited &&
      !this.eventsLoadCanRetry
    ) {
      this.eventsLoadIsPartial = false;
    }
  }

  async loadEventsInDateBatches({ requestId, loadSignature }) {
    this.initializeEventDateBatches(loadSignature);
    const maxSuccessfulBatches = this.shouldAutoLoadAllEventBatches
      ? Number.POSITIVE_INFINITY
      : 1;

    await this.continueEventDateBatches({
      requestId,
      loadSignature,
      maxSuccessfulBatches,
    });
  }

  syncBrowserStateFromModel(model) {
    this.clearSearchDebounceTimer();
    this.clearTransientSubscribeUi();
    this.closeExternalCalendarPanel({ clearQueryParam: false });
    this.resetEventBatchLoadState();
    this.eventsLoadError = model?.eventsLoadError || "";
    this.filter = model?.filter || "upcoming";
    this.start = model?.start || null;
    this.end = model?.end || null;
    this.q = model?.q || "";
    this.searchQuery = this.q;
    this.room_id = model?.room_id || "";
    this.location_id = model?.location_id || "";
    this.category_id = model?.category_id || "";
    this.tag_id = model?.tag_id || "";
    this.tag_ids = tagFilterIds(model?.tag_ids || "").join(",");
    this.owner_id = model?.owner_id || "";
    this.place_id = model?.place_id || "";
    this.place_slug = model?.place_slug || "";
    this.place_name = model?.place_name || "";
    this.event_visibility = normalizeEventVisibility(model?.event_visibility || "");
    this.scope = normalizeScope(model?.scope || "all");
    this.rsvp_status = normalizeRsvpStatus(model?.rsvp_status || "");
    this.create = model?.create || "";
    this.external_calendars = model?.external_calendars || "";
    this.subscribe = model?.subscribe || "";
    this.view = normalizeView(model?.view || this.view);
    this.browserMonth = monthString(parseDateString(this.start || dateString(new Date())));
    this.selectedBrowserDate = "";
    this.closeMonthMore();
    this.timeGridDraft = null;
    this.applyEventDefaults();
    this.lastEventsLoadSignature = this.eventsStateSignature;
    this.pendingEventsLoadSignature = "";

    if (this.create === "1" && this.canCreateEvents) {
      this.isCreateFormExpanded = true;
      this.prefillCreateContextFromFilters();
    }

    if (this.external_calendars === "1" && this.canUseExternalCalendars) {
      this.showExternalCalendarPanel = true;
      this.loadExternalCalendarConnections();
    }

    if (this.subscribe === "1" && this.canUsePersonalFilters) {
      this.subscriptionNotice = "Choose filters, then subscribe to this view.";
    }

    if (this.scope === "my_rsvps") {
      this.maybeCheckEdgeosConnections();
    }

    if (model?.eventsLoadTooLarge) {
      void this.loadEventsForCurrentState({ forceBatched: true });
    }
  }

  async loadEventsForCurrentState({ forceBatched = false } = {}) {
    const requestId = ++this.eventsRequestId;
    const loadSignature = this.eventsStateSignature;
    const scrollY = typeof window === "undefined" ? 0 : window.scrollY || 0;
    const shouldRestoreScroll = scrollY > 0;
    this.pendingEventsLoadSignature = loadSignature;
    this.isLoadingEvents = true;
    this.eventsLoadError = "";
    this.resetEventBatchLoadState();

    try {
      if (forceBatched) {
        await this.loadEventsInDateBatches({ requestId, loadSignature });
        this.lastEventsLoadSignature = loadSignature;
        return;
      }

      const response = await ajax("/calendar/events.json", {
        data: this.eventRequestData(this.activeRangeStart, this.activeRangeEnd),
      });

      if (requestId !== this.eventsRequestId) {
        return;
      }

      this.lastEventsLoadSignature = loadSignature;
      this.applyEventListResponse(response, { replace: true });

      if (shouldRestoreScroll && typeof window !== "undefined") {
        window.requestAnimationFrame(() => window.scrollTo({ top: scrollY, left: 0 }));
      }
    } catch (error) {
      if (requestId !== this.eventsRequestId) {
        return;
      }

      if (isCalendarWorkLimitError(error)) {
        await this.loadEventsInDateBatches({ requestId, loadSignature });
        this.lastEventsLoadSignature = loadSignature;
      } else {
        this.eventsLoadError = extractErrorMessage(
          error,
          i18n("discourse_events_calendar.event_loading.failed")
        );
      }
    } finally {
      if (requestId === this.eventsRequestId) {
        if (this.pendingEventsLoadSignature === loadSignature) {
          this.pendingEventsLoadSignature = "";
        }

        this.isLoadingEvents = false;
      }
    }
  }

  async loadMonthMoreEvents(date) {
    const currentEvents = asArray(this.model?.events);
    const totalEvents = Number(this.model?.month_overflow_counts?.[date] ?? 0);
    const loadedEvents = this.eventsByDateForRange(date, dateString(addDays(parseDateString(date), 1))).get(date) || [];

    if (loadedEvents.length >= totalEvents || this.loadingMonthMoreDate === date) {
      return;
    }

    this.loadingMonthMoreDate = date;

    try {
      const response = await ajax("/calendar/events.json", {
        data: this.eventRequestData(
          date,
          dateString(addDays(parseDateString(date), 1)),
          { compactMonth: false }
        ),
      });

      const events = mergeCalendarEvents(currentEvents, response?.events || response);
      this.model = {
        ...this.model,
        events,
      };
    } catch (error) {
      // Keep the compact visible rows in place, but make the failure explicit.
      // A day that exceeds the server work limit cannot be split further
      // without changing calendar-day semantics.
      if (isCalendarWorkLimitError(error)) {
        this.eventsLoadIsPartial = true;
        this.eventsLoadHardLimited = true;
      } else {
        this.eventsLoadError = extractErrorMessage(
          error,
          i18n("discourse_events_calendar.event_loading.day_failed")
        );
      }
    } finally {
      if (this.loadingMonthMoreDate === date) {
        this.loadingMonthMoreDate = "";
      }
    }
  }

  @action
  async createSubscriptionForCurrentView() {
    if (!this.canUsePersonalFilters || this.isSavingSubscription) {
      return;
    }

    const fallbackName = this.q ? `Calendar: ${this.q}` : "Calendar view";
    const name = window.prompt("Name this calendar subscription", fallbackName);
    if (!name?.trim()) {
      return;
    }

    this.isSavingSubscription = true;
    this.subscriptionNotice = "";
    this.subscriptionError = "";
    this.subscriptionLinks = null;

    try {
      const response = await ajax("/calendar/subscriptions.json", {
        type: "POST",
        data: {
          subscription: {
            name: name.trim(),
            filter: this.currentSubscriptionFilter,
          },
        },
      });
      this.subscriptionLinks = response?.subscription;
      this.subscriptionNotice = "Subscription ready.";
      this.subscribe = "";
    } catch (error) {
      this.subscriptionError = extractErrorMessage(
        error,
        "Calendar subscription could not be created."
      );
      popupAjaxError(error);
    } finally {
      this.isSavingSubscription = false;
    }
  }

  @action
  async copySubscriptionUrl() {
    const url = this.subscriptionLinks?.webcal_url || this.subscriptionLinks?.feed_url;
    if (!url) {
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      this.subscriptionNotice = "Subscription URL copied.";
    } catch {
      this.subscriptionNotice = url;
    }
  }

  @action
  dismissSubscriptionNotice() {
    this.clearTransientSubscribeUi({ clearQueryParam: true });
  }

  clearTransientSubscribeUi({ clearQueryParam = false } = {}) {
    this.subscriptionNotice = "";
    this.subscriptionError = "";
    this.subscriptionLinks = null;

    if (clearQueryParam) {
      this.subscribe = "";
    }
  }

  closeExternalCalendarPanel({ clearQueryParam = true } = {}) {
    this.showExternalCalendarPanel = false;
    this.externalCalendarNotice = "";
    this.externalCalendarError = "";

    if (clearQueryParam) {
      this.external_calendars = "";
    }
  }

  @action
  async toggleExternalCalendarPanel() {
    if (!this.canUseExternalCalendars) {
      return;
    }

    if (this.showExternalCalendarPanel) {
      this.closeExternalCalendarPanel();
      return;
    }

    this.showExternalCalendarPanel = true;
    this.external_calendars = "1";
    this.externalCalendarNotice = "";
    this.externalCalendarError = "";

    if (this.showExternalCalendarPanel) {
      await this.loadExternalCalendarConnections();
    }
  }

  async loadExternalCalendarConnections() {
    if (!this.canUseExternalCalendars || this.isLoadingExternalCalendars) {
      return;
    }

    this.isLoadingExternalCalendars = true;
    this.externalCalendarError = "";

    try {
      const response = await ajax("/calendar/external/connections.json");
      this.externalCalendarConnections = asArray(response?.connections).map((connection) =>
        externalCalendarConnectionView(connection)
      );
      this.googleCalendarConfigured = response?.google_configured === true;
    } catch (error) {
      this.externalCalendarError = extractErrorMessage(
        error,
        "External calendars could not be loaded."
      );
      popupAjaxError(error);
    } finally {
      this.isLoadingExternalCalendars = false;
    }
  }

  @action
  async syncExternalConnection(connection) {
    if (!connection?.id || connection.is_syncing) {
      return;
    }

    this.setExternalConnectionState(connection.id, { is_syncing: true });
    this.externalCalendarNotice = "";
    this.externalCalendarError = "";

    try {
      const response = await ajax(
        `/calendar/external/connections/${encodeURIComponent(connection.id)}/sync_now.json`,
        { type: "POST" }
      );
      const nextConnection = response?.connection || connection;
      this.replaceExternalConnection(
        externalCalendarConnectionView(nextConnection, { is_syncing: false })
      );
      this.externalCalendarNotice = response?.already_running
        ? "Sync already in progress."
        : "Sync queued.";
    } catch (error) {
      this.setExternalConnectionState(connection.id, { is_syncing: false });
      this.externalCalendarError = extractErrorMessage(
        error,
        "External calendar sync could not be queued."
      );
      popupAjaxError(error);
    }
  }

  @action
  async disconnectExternalConnection(connection) {
    if (!connection?.id || connection.is_deleting) {
      return;
    }

    const providerLabel = connection.provider_label || "external calendar";
    const confirmMessage =
      connection.provider === "edgeos"
        ? "Disconnect EdgeOS? RSVPs synced from EdgeOS will be removed from your calendar."
        : `Disconnect ${providerLabel}?`;
    if (typeof window !== "undefined" && !window.confirm(confirmMessage)) {
      return;
    }

    this.setExternalConnectionState(connection.id, { is_deleting: true });
    this.externalCalendarNotice = "";
    this.externalCalendarError = "";

    try {
      await ajax(`/calendar/external/connections/${encodeURIComponent(connection.id)}.json`, {
        type: "DELETE",
      });
      this.externalCalendarConnections = this.externalCalendarConnections.filter(
        (existing) => `${existing.id}` !== `${connection.id}`
      );
      this.externalCalendarNotice = `${providerLabel} disconnected.`;
    } catch (error) {
      this.setExternalConnectionState(connection.id, { is_deleting: false });
      this.externalCalendarError = extractErrorMessage(
        error,
        "External calendar could not be disconnected."
      );
      popupAjaxError(error);
    }
  }

  setExternalConnectionState(connectionId, state) {
    this.externalCalendarConnections = this.externalCalendarConnections.map((connection) =>
      `${connection.id}` === `${connectionId}` ? { ...connection, ...state } : connection
    );
  }

  replaceExternalConnection(nextConnection) {
    this.externalCalendarConnections = this.externalCalendarConnections.map((connection) =>
      `${connection.id}` === `${nextConnection.id}` ? nextConnection : connection
    );
  }

  async resumeEventDateBatches(maxSuccessfulBatches) {
    if (
      this.isLoadingEvents ||
      this.eventBatchLoadSignature !== this.eventsStateSignature ||
      this.eventBatchQueue.length === 0
    ) {
      return;
    }

    const requestId = this.eventsRequestId;
    const loadSignature = this.eventBatchLoadSignature;
    this.isLoadingEvents = true;
    this.eventsLoadCanRetry = false;
    this.eventsLoadError = "";

    try {
      await this.continueEventDateBatches({
        requestId,
        loadSignature,
        maxSuccessfulBatches,
      });
    } finally {
      if (requestId === this.eventsRequestId) {
        this.isLoadingEvents = false;
      }
    }
  }

  @action
  async loadMoreEventBatches() {
    await this.resumeEventDateBatches(1);
  }

  @action
  async loadAllEventBatches() {
    await this.resumeEventDateBatches(Number.POSITIVE_INFINITY);
  }

  @action
  async retryEventBatches() {
    const maxSuccessfulBatches = this.shouldAutoLoadAllEventBatches
      ? Number.POSITIVE_INFINITY
      : 1;
    await this.resumeEventDateBatches(maxSuccessfulBatches);
  }

  @action
  async retryEventsLoad() {
    await this.loadEventsForCurrentState();
  }

  @action
  refineEventResults() {
    this.isMobileFiltersExpanded = true;
    const filterPanel = document.querySelector(
      ".discourse-events-calendar__browser-filters"
    );
    filterPanel?.scrollIntoView({ behavior: "smooth", block: "start" });
    filterPanel?.querySelector("input, select, button")?.focus();
  }

  @action
  ensureEventsForCurrentQueryParams() {
    const signature = this.eventsStateSignature;

    if (
      signature !== this.lastEventsLoadSignature &&
      signature !== this.pendingEventsLoadSignature
    ) {
      this.loadEventsForCurrentState();
    }
  }

  commitSearchFilter() {
    const nextQuery = this.searchQuery.trim();

    if (this.q === nextQuery && this.lastEventsLoadSignature === this.eventsStateSignature) {
      return;
    }

    this.q = nextQuery;
    this.selectedBrowserDate = "";
    this.loadEventsForCurrentState();
  }

  clearSearchDebounceTimer() {
    if (!this.searchDebounceTimer) {
      return;
    }

    window.clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = null;
  }

  scheduleSearchCommit() {
    this.clearSearchDebounceTimer();
    this.searchDebounceTimer = window.setTimeout(() => {
      this.searchDebounceTimer = null;
      this.commitSearchFilter();
    }, 700);
  }

  applyFullCalendarState({ view, start, end }) {
    const normalizedView = normalizeView(view);
    const normalizedStart = start instanceof Date ? dateString(start) : start;
    const normalizedEnd = end instanceof Date ? dateString(end) : end;

    if (
      this.view === normalizedView &&
      this.activeRangeStart === normalizedStart &&
      this.activeRangeEnd === normalizedEnd
    ) {
      return;
    }

    this.view = normalizedView;
    this.start = normalizedStart;
    this.end = normalizedEnd;
    this.browserMonth = monthString(parseDateString(normalizedStart));
    this.selectedBrowserDate = "";
    this.closeMonthMore();
    this.timeGridDraft = null;
    this.loadEventsForCurrentState();
  }

  selectedLocationRecord() {
    return this.locations.find((location) =>
      [locationValue(location), location.slug, location.name]
        .filter(Boolean)
        .includes(this.selectedLocationId)
    );
  }

  selectedLocationFilterRecord() {
    return this.locations.find((location) =>
      [locationValue(location), location.slug, location.name]
        .filter(Boolean)
        .includes(`${this.location_id}`)
    );
  }

  roomMatchesLocation(room, location) {
    if (!location) {
      return true;
    }

    if (room.location_id && `${room.location_id}` === `${locationRecordId(location)}`) {
      return true;
    }

    const aliases = [location.name, location.address, location.slug].filter(Boolean);
    return aliases.some((value) => value === room.location);
  }

  roomMatchesLocationFilter(room) {
    if (!this.location_id) {
      return true;
    }

    return this.roomMatchesLocation(room, this.selectedLocationFilterRecord());
  }

  roomMatchesSelectedLocation(room) {
    if (!this.selectedLocationId) {
      return true;
    }

    return this.roomMatchesLocation(room, this.selectedLocationRecord());
  }

  selectedLocationForRoom(room) {
    if (room?.location_id) {
      const match = this.locations.find((location) => `${locationRecordId(location)}` === `${room.location_id}`);
      if (match) {
        return match;
      }
    }

    return this.locations.find((location) =>
      [location.name, location.address, location.slug].filter(Boolean).includes(room.location)
    );
  }

  selectedLocationPublicAddress(location = this.selectedLocationRecord()) {
    return location?.address || location?.name || "";
  }

  selectedLocationRoomName(location = this.selectedLocationRecord()) {
    return location?.name || location?.address || "";
  }

  get defaultEventCategoryId() {
    return normalizedIdString(this.model?.defaults?.category_id);
  }

  get effectiveCreateCategoryId() {
    return normalizedIdString(this.selectedCategoryId) || this.defaultEventCategoryId;
  }

  categoryIsInSelectedVenueScope(categoryId) {
    const selectedCategoryId = this.effectiveCreateCategoryId;
    const candidateId = normalizedIdString(categoryId);

    if (!selectedCategoryId) {
      return true;
    }

    if (!candidateId) {
      return false;
    }

    if (candidateId === selectedCategoryId) {
      return true;
    }

    let currentCategory = this.categories.find((category) => `${category.id}` === candidateId);
    const seenCategoryIds = new Set();

    while (currentCategory?.parent_category_id) {
      const parentId = normalizedIdString(currentCategory.parent_category_id);
      if (!parentId || seenCategoryIds.has(parentId)) {
        break;
      }

      if (parentId === selectedCategoryId) {
        return true;
      }

      seenCategoryIds.add(parentId);
      currentCategory = this.categories.find((category) => `${category.id}` === parentId);
    }

    return false;
  }

  categoryIdsMatchCreateFilter(categoryIds, { allowUnscoped = false } = {}) {
    if (!this.effectiveCreateCategoryId) {
      return true;
    }

    const ids = asArray(categoryIds).map(normalizedIdString).filter(Boolean);
    if (ids.length === 0) {
      return allowUnscoped;
    }

    return ids.some((categoryId) => this.categoryIsInSelectedVenueScope(categoryId));
  }

  venueMatchesSelectedCategory(venue) {
    return this.categoryIdsMatchCreateFilter(categoryIdsForRecord(venue), {
      allowUnscoped: !this.hasCategoryScopedVenues,
    });
  }

  placeMatchesSelectedCategory(place) {
    return this.categoryIdsMatchCreateFilter(categoryIdsForRecord(place));
  }

  roomMatchesSelectedCategory(room) {
    const location = this.selectedLocationForRoom(room) || room?.location_record;
    return location ? this.venueMatchesSelectedCategory(location) : this.venueMatchesSelectedCategory(room);
  }

  syncVenueSelectionForCategory() {
    const selectedLocation = this.selectedLocationRecord();
    if (this.selectedLocationId && selectedLocation && !this.venueMatchesSelectedCategory(selectedLocation)) {
      if (this.location === this.roomLocationApplied) {
        this.location = "";
        this.roomLocationApplied = "";
      }
      this.selectedLocationId = "";
      this.selectedRoomId = "";
      this.clearRoomAvailability();
    }

    const selectedRoom = this.rooms.find((room) => `${room.id}` === `${this.selectedRoomId}`);
    if (this.selectedRoomId && selectedRoom && !this.roomMatchesSelectedCategory(selectedRoom)) {
      this.selectedRoomId = "";
      this.clearRoomAvailability();
    }

    if (
      this.placeId &&
      this.selectedPlaceCategoryIds.length > 0 &&
      !this.categoryIdsMatchCreateFilter(this.selectedPlaceCategoryIds)
    ) {
      this.clearPlace();
    }
  }

  async loadPlaceOptions(query) {
    const value = `${query || ""}`.trim();

    if (!this.placesEnabled || value.length < 2) {
      this.placeOptions = [];
      this.isLoadingPlaces = false;
      return;
    }

    const requestId = ++this.placeRequestId;
    this.isLoadingPlaces = true;

    try {
      const categoryId = this.effectiveCreateCategoryId;
      const data = { q: value, limit: categoryId ? 25 : 8 };
      if (categoryId) {
        data.category_id = categoryId;
      }

      const response = await ajax("/places/autocomplete.json", {
        data,
      });

      if (requestId !== this.placeRequestId) {
        return;
      }

      this.placeOptions = asArray(response?.places)
        .map(normalizePlaceOption)
        .filter(Boolean)
        .filter((place) => this.placeMatchesSelectedCategory(place))
        .slice(0, 8);
    } catch {
      if (requestId === this.placeRequestId) {
        this.placeOptions = [];
      }
    } finally {
      if (requestId === this.placeRequestId) {
        this.isLoadingPlaces = false;
      }
    }
  }

  applyPlaceToCreateForm(place) {
    const selectedPlace = normalizePlaceOption(place);
    if (!selectedPlace) {
      return;
    }

    this.venueType = "existing";
    this.meetingUrl = "";
    this.customLocationUrl = "";
    this.placeId = `${selectedPlace.id}`;
    this.selectedPlaceCategoryIds = categoryIdsForRecord(selectedPlace);
    this.placeQuery = selectedPlace.name;
    this.placeOptions = [];
    this.showVenueSearch = false;

    const publicLocation = selectedPlace.address || selectedPlace.name;
    if (publicLocation && (!this.location || this.location === this.roomLocationApplied)) {
      this.location = publicLocation;
      this.roomLocationApplied = publicLocation;
    }
  }

  async prefillPlaceFromParams() {
    if (!this.placesEnabled || !this.place_id) {
      return;
    }

    const requestId = ++this.placePrefillRequestId;
    const fallbackPlace = {
      id: this.place_id,
      name: this.place_name || this.place_slug || `Place ${this.place_id}`,
    };

    this.applyPlaceToCreateForm(fallbackPlace);

    try {
      const placeKey = this.place_slug || this.place_id;
      const response = await ajax(`/places/${encodeURIComponent(placeKey)}.json`);

      if (requestId !== this.placePrefillRequestId) {
        return;
      }

      this.applyPlaceToCreateForm(response?.place || response);
    } catch {
      // Keep the route-provided fallback selected when the richer Places
      // payload is unavailable. The create request will still validate place_id.
    }
  }

  async loadRoleUserOptions(query) {
    const value = roleSearchTerm(query);

    if (value.length < 2) {
      this.roleUserOptions = [];
      this.isLoadingRoleUsers = false;
      return;
    }

    const requestId = ++this.roleUserRequestId;
    this.isLoadingRoleUsers = true;

    try {
      const response = await ajax("/u/search/users.json", {
        data: { term: value, limit: 8 },
      });

      if (requestId !== this.roleUserRequestId) {
        return;
      }

      this.roleUserOptions = asArray(response?.users)
        .map(normalizeUserOption)
        .filter(Boolean);
    } catch {
      if (requestId === this.roleUserRequestId) {
        this.roleUserOptions = [];
      }
    } finally {
      if (requestId === this.roleUserRequestId) {
        this.isLoadingRoleUsers = false;
      }
    }
  }

  applyLocationToCreateForm(location) {
    if (!location) {
      return;
    }

    this.venueType = "existing";
    this.meetingUrl = "";
    this.customLocationUrl = "";
    this.selectedLocationId = locationValue(location);
    if (!this.location || this.location === this.roomLocationApplied) {
      this.location = this.selectedLocationPublicAddress(location);
      this.roomLocationApplied = this.location;
    }
    if (!this.roomLocation) {
      this.roomLocation = this.selectedLocationRoomName(location);
    }
  }

  applyRoomToCreateForm(room) {
    if (!room) {
      return;
    }

    this.venueType = "existing";
    this.meetingUrl = "";
    this.customLocationUrl = "";
    this.selectedRoomId = `${room.id}`;
    const matchingLocation = this.selectedLocationForRoom(room);
    if (matchingLocation) {
      this.applyLocationToCreateForm(matchingLocation);
    } else if (room.location && (!this.location || this.location === this.roomLocationApplied)) {
      this.location = room.location;
      this.roomLocationApplied = room.location;
    }
    this.scheduleRoomAvailabilityCheck();
  }

  ensureDefaultHostNames() {
    if (!this.hostNames.trim()) {
      this.hostNames = currentUserParticipantName(this.currentUser);
    }
  }

  prefillCreateContextFromFilters() {
    this.ensureDefaultHostNames();
    this.prefillPlaceFromParams();

    if (this.location_id) {
      this.applyLocationToCreateForm(this.selectedLocationFilterRecord());
    }

    if (this.room_id) {
      const room = this.rooms.find((candidate) => `${candidate.id}` === `${this.room_id}`);
      this.applyRoomToCreateForm(room);
    }
  }

  availabilityStartParam() {
    return this.allDay ? `${this.eventDate}T00:00` : `${this.eventDate}T${this.startTime}`;
  }

  availabilityEndParam() {
    return this.allDay ? `${nextDayString(this.eventDate)}T00:00` : `${this.endDate}T${this.endTime}`;
  }

  clearRoomAvailability() {
    this.roomAvailabilityStatus = "";
    this.roomAvailabilityMessage = "";
    this.roomAvailabilityConflict = null;
  }

  scheduleRoomAvailabilityCheck() {
    if (!this.selectedRoomId || !this.hasValidTimeRange) {
      this.clearRoomAvailability();
      return;
    }

    this.roomAvailabilityStatus = "checking";
    this.roomAvailabilityMessage = "Checking room availability...";
    this.roomAvailabilityConflict = null;
    debounce(this, this.checkRoomAvailability, 350);
  }

  async checkRoomAvailability() {
    if (!this.selectedRoomId || !this.hasValidTimeRange) {
      this.clearRoomAvailability();
      return;
    }

    const requestId = ++this.roomAvailabilityRequestId;
    const room = this.rooms.find((candidate) => `${candidate.id}` === `${this.selectedRoomId}`);

    try {
      const response = await ajax("/calendar/reservations/availability.json", {
        data: {
          room_id: this.selectedRoomId,
          start: this.availabilityStartParam(),
          end: this.availabilityEndParam(),
          timezone: normalizeTimeZone(this.timezone, browserTimeZone()),
        },
      });

      if (requestId !== this.roomAvailabilityRequestId) {
        return;
      }

      if (response?.available) {
        this.roomAvailabilityStatus = "available";
        this.roomAvailabilityMessage = `${room?.name || i18n("discourse_events_calendar.labels.room")} is available for this time.`;
        this.roomAvailabilityConflict = null;
      } else {
        this.roomAvailabilityStatus = "unavailable";
        this.roomAvailabilityConflict = response?.conflicts?.[0] || response?.conflict || null;
        this.roomAvailabilityMessage =
          response?.message ||
          `${room?.name || i18n("discourse_events_calendar.labels.room")} is already reserved ${formatConflictRange(
            this.roomAvailabilityConflict
          )}.`;
      }
    } catch (error) {
      if (requestId !== this.roomAvailabilityRequestId) {
        return;
      }

      this.roomAvailabilityStatus = "error";
      this.roomAvailabilityConflict = null;
      this.roomAvailabilityMessage = extractErrorMessage(
        error,
        "Room availability could not be checked."
      );
    }
  }

  @action
  updatePlaceQuery(event) {
    this.placeQuery = event.target.value;
    this.placeId = "";
    this.selectedPlaceCategoryIds = [];
    debounce(this, this.loadPlaceOptions, this.placeQuery, 250);
  }

  @action
  selectPlace(place, event) {
    event?.preventDefault();
    const selectedPlace = normalizePlaceOption(place);
    if (!selectedPlace) {
      return;
    }

    this.venueType = "existing";
    this.meetingUrl = "";
    this.customLocationUrl = "";
    this.placeId = `${selectedPlace.id}`;
    this.selectedPlaceCategoryIds = categoryIdsForRecord(selectedPlace);
    this.placeQuery = selectedPlace.name;
    this.placeOptions = [];
    this.showVenueSearch = false;

    const publicLocation = selectedPlace.address || selectedPlace.name;
    if (publicLocation && (!this.location || this.location === this.roomLocationApplied)) {
      this.location = publicLocation;
      this.roomLocationApplied = publicLocation;
    }
  }

  @action
  clearPlace(event) {
    event?.preventDefault();
    this.placeQuery = "";
    this.placeId = "";
    this.selectedPlaceCategoryIds = [];
    this.placeOptions = [];
    this.isLoadingPlaces = false;
  }

  @action
  toggleVenueSearch(event) {
    event?.preventDefault();

    this.showVenueSearch = !this.showVenueSearch;
    if (this.showVenueSearch) {
      this.closeVenueFinder();
    } else {
      this.placeOptions = [];
      this.isLoadingPlaces = false;
    }
  }

  @action
  updateVenueType(event) {
    const nextType = normalizeVenueType(event.target.value);
    this.venueType = nextType;
    this.closeVenueFinder();
    this.showVenueSearch = false;
    this.showRoomForm = false;

    if (nextType === "existing") {
      this.meetingUrl = "";
      this.customLocationUrl = "";
      if (["Venue TBD", "Virtual event", "Meeting"].includes(this.location)) {
        this.location = "";
      }
      return;
    }

    this.selectedLocationId = "";
    this.selectedRoomId = "";
    this.placeId = "";
    this.selectedPlaceCategoryIds = [];
    this.placeQuery = "";
    this.placeOptions = [];
    this.clearRoomAvailability();

    if (nextType === "virtual") {
      this.customLocationUrl = "";
      this.location = "Virtual event";
      this.roomLocationApplied = this.location;
    } else if (nextType === "custom") {
      this.meetingUrl = "";
      if (["Venue TBD", "Virtual event", "Meeting"].includes(this.location)) {
        this.location = "";
        this.roomLocationApplied = "";
      }
    } else {
      this.meetingUrl = "";
      this.customLocationUrl = "";
      this.location = "Venue TBD";
      this.roomLocationApplied = this.location;
    }
  }

  @action
  updateMeetingUrl(event) {
    this.meetingUrl = event.target.value;
  }

  @action
  updateCustomLocation(event) {
    this.location = event.target.value;
  }

  venueFinderMissingFieldsForCurrentForm() {
    const missingFields = [];

    if (!this.eventDate) {
      missingFields.push("starts_at");
    }

    if (!this.allDay && (!this.startTime || !this.endTime || !this.hasValidTimeRange)) {
      missingFields.push("ends_at");
    }

    return missingFields;
  }

  clearVenueFinderResults() {
    this.venueSuggestions = [];
    this.venueFinderError = "";
    this.venueFinderMissingFields = [];
  }

  scheduleVenueSuggestionsLoad() {
    if (!this.showVenueFinder) {
      return;
    }

    debounce(this, this.loadVenueSuggestions, 350);
  }

  async loadVenueSuggestions() {
    if (!this.showVenueFinder) {
      return;
    }

    const missingFields = this.venueFinderMissingFieldsForCurrentForm();
    if (missingFields.length > 0) {
      this.venueSuggestions = [];
      this.venueFinderError = "";
      this.venueFinderMissingFields = missingFields;
      this.isLoadingVenueSuggestions = false;
      return;
    }

    const requestId = ++this.venueFinderRequestId;
    this.isLoadingVenueSuggestions = true;
    this.venueFinderError = "";
    this.venueFinderMissingFields = [];

    try {
      const response = await ajax("/calendar/venue_suggestions.json", {
        type: "POST",
        data: {
          starts_at: this.availabilityStartParam(),
          ends_at: this.availabilityEndParam(),
          timezone: normalizeTimeZone(this.timezone, browserTimeZone()),
          capacity: this.capacity,
          category_id: this.effectiveCreateCategoryId,
          limit: 8,
        },
      });

      if (requestId !== this.venueFinderRequestId) {
        return;
      }

      this.venueSuggestions = asArray(response?.suggestions)
        .map(normalizeVenueSuggestion)
        .filter(Boolean);
    } catch (error) {
      if (requestId !== this.venueFinderRequestId) {
        return;
      }

      const responseJSON = error?.jqXHR?.responseJSON || error?.responseJSON;
      this.venueSuggestions = [];
      this.venueFinderMissingFields = asArray(responseJSON?.missing_fields);
      this.venueFinderError = this.hasVenueFinderMissingFields
        ? ""
        : extractErrorMessage(error, "Venues could not be checked.");
    } finally {
      if (requestId === this.venueFinderRequestId) {
        this.isLoadingVenueSuggestions = false;
      }
    }
  }

  @action
  toggleVenueFinder(event) {
    event?.preventDefault();

    if (this.showVenueFinder) {
      if (this.isLoadingVenueSuggestions) {
        return;
      }

      this.closeVenueFinder();
    } else {
      this.showVenueFinder = true;
      this.showVenueSearch = false;
      this.placeOptions = [];
      this.loadVenueSuggestions();
    }
  }

  @action
  closeVenueFinder(event) {
    event?.preventDefault?.();
    this.showVenueFinder = false;
    this.isLoadingVenueSuggestions = false;
    this.clearVenueFinderResults();
  }

  @action
  refreshVenueSuggestions(event) {
    event?.preventDefault();
    this.loadVenueSuggestions();
  }

  @action
  applyVenueSuggestion(suggestion, event) {
    event?.preventDefault();

    const selectedSuggestion = normalizeVenueSuggestion(suggestion);
    if (!selectedSuggestion) {
      return;
    }

    if (selectedSuggestion.place) {
      this.applyPlaceToCreateForm(selectedSuggestion.place);
    }

    if (selectedSuggestion.location) {
      this.applyLocationToCreateForm(selectedSuggestion.location);
    }

    if (selectedSuggestion.room) {
      this.applyRoomToCreateForm({
        ...selectedSuggestion.room,
        location_record: selectedSuggestion.location,
      });
    }

    if (selectedSuggestion.publicLocation) {
      this.location = selectedSuggestion.publicLocation;
      this.roomLocationApplied = selectedSuggestion.publicLocation;
    }

    this.closeVenueFinder();
  }

  @action
  selectLocation(event) {
    this.selectedLocationId = event.target.value;
    this.venueType = "existing";
    this.meetingUrl = "";
    this.customLocationUrl = "";
    this.placeId = "";
    this.selectedPlaceCategoryIds = [];
    this.placeQuery = "";
    this.placeOptions = [];
    const selectedLocation = this.selectedLocationRecord();

    if (selectedLocation) {
      this.location = this.selectedLocationPublicAddress(selectedLocation);
      this.roomLocationApplied = this.location;

      if (!this.roomLocation) {
        this.roomLocation = this.selectedLocationRoomName(selectedLocation);
      }
    }

    if (this.selectedRoomId) {
      const selectedRoom = this.rooms.find((room) => `${room.id}` === `${this.selectedRoomId}`);
      if (selectedRoom && !this.roomMatchesSelectedLocation(selectedRoom)) {
        this.selectedRoomId = "";
        this.roomLocationApplied = "";
        this.clearRoomAvailability();
      }
    }
  }

  @action
  selectRoom(event) {
    this.selectedRoomId = event.target.value;
    this.venueType = "existing";
    this.meetingUrl = "";
    this.customLocationUrl = "";
    this.placeId = "";
    this.selectedPlaceCategoryIds = [];
    this.placeQuery = "";
    this.placeOptions = [];

    const room = this.rooms.find((candidate) => `${candidate.id}` === this.selectedRoomId);

    if (!room) {
      if (this.location && this.location === this.roomLocationApplied) {
        this.location = "";
      }

      this.roomLocationApplied = "";
      this.clearRoomAvailability();
      return;
    }

    const matchingLocation = this.selectedLocationForRoom(room);
    const publicLocation = matchingLocation
      ? this.selectedLocationPublicAddress(matchingLocation)
      : room.location;

    if (room.location && (!this.location || this.location === this.roomLocationApplied)) {
      this.location = publicLocation;
      this.roomLocationApplied = publicLocation;
    }

    if (matchingLocation) {
      this.selectedLocationId = locationValue(matchingLocation);
    }

    this.scheduleRoomAvailabilityCheck();
    this.scheduleVenueSuggestionsLoad();
  }

  setFilter(filter) {
    this.dismissEventPreview();
    this.filter = filter;
    this.selectedBrowserDate = "";
    this.closeMonthMore();
    this.timeGridDraft = null;
    this.createNotice = "";
    this.loadEventsForCurrentState();
  }

  eventMatchesActiveFilters(event) {
    if (!eventOverlapsRange(event, this.start || this.model?.start, this.end || this.model?.end)) {
      return false;
    }

    if (
      this.activeScope === "my_rsvps" &&
      !["going", "interested", "waitlisted"].includes(event.current_user_rsvp_status)
    ) {
      return false;
    }

    if (
      this.activeScope === "organizing" &&
      `${event.owner?.id}` !== `${this.model?.current_user_id}`
    ) {
      return false;
    }

    if (
      this.activeRsvpStatus &&
      event.current_user_rsvp_status !== this.activeRsvpStatus
    ) {
      return false;
    }

    if (this.room_id && `${event.room?.id}` !== `${this.room_id}`) {
      return false;
    }

    if (this.location_id) {
      const location = this.selectedLocationFilterRecord();
      const roomMatches = event.room && this.roomMatchesLocation(event.room, location);
      const eventMatches =
        location &&
        (`${event.location_id}` === `${locationRecordId(location)}` ||
          [location.name, location.address, location.slug].filter(Boolean).includes(event.location));

      if (!roomMatches && !eventMatches) {
        return false;
      }
    }

    if (this.category_id && !this.categoryMatchesSelectedFilter(event.category_id)) {
      return false;
    }

    const selectedTagIds = this.selectedTagIds;
    if (
      selectedTagIds.length > 0 &&
      !asArray(event.tags).some((tag) => selectedTagIds.includes(`${tag.id}`))
    ) {
      return false;
    }

    if (this.owner_id && `${event.owner?.id}` !== `${this.owner_id}`) {
      return false;
    }

    if (this.activeEventVisibility && event.visibility !== this.activeEventVisibility) {
      return false;
    }

    if (!eventMatchesSearch(event, this.q)) {
      return false;
    }

    if (this.isAllFilter) {
      return true;
    }

    return this.isPastFilter ? eventIsPast(event) : !eventIsPast(event);
  }

  @action
  showUpcomingEvents() {
    this.setFilter("upcoming");
  }

  @action
  showPastEvents() {
    this.setFilter("past");
  }

  @action
  showAllEvents() {
    this.setFilter("all");
  }

  @action
  updateTimeFilter(event) {
    this.setFilter(event.target.value);
  }

  setScope(scope) {
    this.dismissEventPreview();
    this.scope = normalizeScope(scope);
    this.selectedBrowserDate = "";
    this.closeMonthMore();
    this.timeGridDraft = null;
    this.createNotice = "";
    if (this.scope === "my_rsvps") {
      this.maybeCheckEdgeosConnections();
    }
    this.loadEventsForCurrentState();
  }

  personalFilterButtonClass(isActive) {
    const classes = ["btn", "btn-small", "discourse-events-calendar__personal-filter-button"];

    if (isActive) {
      classes.push("discourse-events-calendar__personal-filter-button--active");
    }

    return classes.join(" ");
  }

  @action
  showAllCommunityEvents() {
    this.setScope("all");
  }

  @action
  showMyRsvps() {
    this.setScope("my_rsvps");
  }

  @action
  showOrganizingEvents() {
    this.setScope("organizing");
  }

  @action
  toggleMyRsvpsScope() {
    this.setScope(this.isMyRsvpsScope ? "all" : "my_rsvps");
  }

  @action
  toggleOrganizingScope() {
    this.setScope(this.isOrganizingScope ? "all" : "organizing");
  }

  @action
  showMonthView() {
    this.view = "month";
    this.setBrowserMonth(parseDateString(this.activeRangeStart));
  }

  @action
  showWeekView() {
    this.view = "week";
    this.setBrowserWeek(parseDateString(this.browserFocusDate));
  }

  @action
  showDayView() {
    this.view = "day";
    this.setBrowserDay(parseDateString(this.browserFocusDate));
  }

  @action
  showVenueView() {
    this.view = "venue";
    this.setBrowserDay(parseDateString(this.browserFocusDate));
  }

  @action
  showAgendaView() {
    this.view = "agenda";
    this.setBrowserMonth(parseDateString(this.browserFocusDate));
  }

  setBrowserMonth(month) {
    this.dismissEventPreview();
    const normalizedMonth = monthString(month);
    const range = monthRange(normalizedMonth);

    this.browserMonth = normalizedMonth;
    this.start = range.start;
    this.end = range.end;
    this.selectedBrowserDate = "";
    this.closeMonthMore();
    this.timeGridDraft = null;
    this.loadEventsForCurrentState();
  }

  setBrowserWeek(date) {
    this.dismissEventPreview();
    const range = weekRange(dateString(date));

    this.browserMonth = monthString(parseDateString(range.start));
    this.start = range.start;
    this.end = range.end;
    this.selectedBrowserDate = "";
    this.closeMonthMore();
    this.timeGridDraft = null;
    this.loadEventsForCurrentState();
  }

  setBrowserDay(date) {
    this.dismissEventPreview();
    const range = dayRange(dateString(date));

    this.browserMonth = monthString(parseDateString(range.start));
    this.start = range.start;
    this.end = range.end;
    this.selectedBrowserDate = "";
    this.closeMonthMore();
    this.timeGridDraft = null;
    this.loadEventsForCurrentState();
  }

  @action
  previousBrowserMonth() {
    const date = parseDateString(this.activeRangeStart);

    if (this.isWeekView) {
      this.setBrowserWeek(addDays(date, -7));
      return;
    }

    if (this.isDayView || this.isVenueView) {
      this.setBrowserDay(addDays(date, -1));
      return;
    }

    date.setMonth(date.getMonth() - 1);
    this.setBrowserMonth(date);
  }

  @action
  nextBrowserMonth() {
    const date = parseDateString(this.activeRangeStart);

    if (this.isWeekView) {
      this.setBrowserWeek(addDays(date, 7));
      return;
    }

    if (this.isDayView || this.isVenueView) {
      this.setBrowserDay(addDays(date, 1));
      return;
    }

    date.setMonth(date.getMonth() + 1);
    this.setBrowserMonth(date);
  }

  @action
  todayBrowserMonth() {
    if (this.isWeekView) {
      this.setBrowserWeek(new Date());
      return;
    }

    if (this.isDayView || this.isVenueView) {
      this.setBrowserDay(new Date());
      return;
    }

    this.setBrowserMonth(new Date());
  }

  @action
  selectBrowserDate(date) {
    this.view = "day";
    this.setBrowserDay(parseDateString(date));
    this.selectQuickDate(date);
    this.createNotice = "";
  }

  @action
  openDayFromMonthCell(date, event) {
    if (event?.target?.closest?.("a, button, input, select, textarea")) {
      return;
    }

    this.selectBrowserDate(date);
  }

  @action
  openMonthMore(date, event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.dismissEventPreview();

    if (this.openMonthMoreDate === date) {
      this.closeMonthMore();
      return;
    }

    this.openMonthMoreDate = date;
    this.loadMonthMoreEvents(date);
    document.addEventListener("mousedown", this.handleMonthMoreDocumentMouseDown, true);
  }

  @action
  closeMonthMore(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.openMonthMoreDate = "";
    document.removeEventListener("mousedown", this.handleMonthMoreDocumentMouseDown, true);
  }

  @action
  toggleAdvancedFilters() {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  @action
  toggleMobileFilters() {
    this.isMobileFiltersExpanded = !this.isMobileFiltersExpanded;
  }

  @action
  keepMonthMoreOpen(event) {
    event?.stopPropagation?.();
  }

  @action
  handleMonthMoreDocumentMouseDown(event) {
    const target = event.target;

    if (
      target?.closest?.(".discourse-events-calendar__month-more-popover") ||
      target?.closest?.(".discourse-events-calendar__month-more") ||
      target?.closest?.(".discourse-events-calendar__event-popover")
    ) {
      return;
    }

    this.closeMonthMore();
  }

  dismissEventPreview() {
    this.clearEventPreviewShowTimer();
    this.clearEventPreviewDismissTimer();
    this.selectedPreviewEvent = null;
    this.eventPreviewStyle = "";
    this.eventPreviewSource = "";
    this.eventPreviewDate = "";
    document.removeEventListener("mousedown", this.handlePreviewDocumentMouseDown, true);
  }

  clearEventPreviewShowTimer() {
    if (!this.eventPreviewShowTimer) {
      return;
    }

    window.clearTimeout(this.eventPreviewShowTimer);
    this.eventPreviewShowTimer = null;
    this.eventPreviewShowKey = "";
  }

  clearEventPreviewDismissTimer() {
    if (!this.eventPreviewDismissTimer) {
      return;
    }

    window.clearTimeout(this.eventPreviewDismissTimer);
    this.eventPreviewDismissTimer = null;
  }

  scheduleEventPreviewShow(eventRecord, event, options = {}) {
    const trigger = event?.currentTarget;

    if (!trigger) {
      return;
    }

    this.clearEventPreviewShowTimer();
    this.clearEventPreviewDismissTimer();

    const key = previewEventKey(eventRecord);
    const delayedEvent = {
      currentTarget: trigger,
      clientX: event?.clientX,
      clientY: event?.clientY,
      type: "dec-delayed-hover",
    };

    this.eventPreviewShowKey = key;
    this.eventPreviewShowTimer = window.setTimeout(() => {
      this.eventPreviewShowTimer = null;

      if (this.eventPreviewShowKey !== key) {
        return;
      }

      this.eventPreviewShowKey = "";
      this.showEventPreview(eventRecord, delayedEvent, {
        ...options,
        immediate: true,
      });
    }, EVENT_PREVIEW_SHOW_DELAY_MS);
  }

  @action
  openEventDetail(eventRecord, event) {
    this.clearEventPreviewShowTimer();

    if (
      event &&
      this.suppressedEventClickKey &&
      this.suppressedEventClickKey === previewEventKey(eventRecord)
    ) {
      event.preventDefault?.();
      event.stopPropagation?.();
      this.suppressedEventClickKey = "";
      return;
    }

    if (
      event?.metaKey ||
      event?.ctrlKey ||
      event?.shiftKey ||
      event?.altKey ||
      event?.button > 0
    ) {
      return;
    }

    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.dismissEventPreview();
    this.closeMonthMore();

    if (eventRecord?.id) {
      this.router.transitionTo("discourse-events-calendar-event", eventRecord.id);
    }
  }

  @action
  cancelPendingEventPreview() {
    this.clearEventPreviewShowTimer();
  }

  @action
  openEventPreview(eventRecord, event) {
    if (
      event &&
      this.suppressedEventClickKey &&
      this.suppressedEventClickKey === previewEventKey(eventRecord)
    ) {
      event.preventDefault?.();
      event.stopPropagation?.();
      this.suppressedEventClickKey = "";
      return;
    }

    if (
      event?.metaKey ||
      event?.ctrlKey ||
      event?.shiftKey ||
      event?.altKey ||
      event?.button > 0
    ) {
      return;
    }

    event?.preventDefault();
    event?.stopPropagation();

    const monthMorePopover = event?.currentTarget?.closest?.(
      ".discourse-events-calendar__month-more-popover"
    );

    if (monthMorePopover) {
      this.monthMorePreviewScrollTop = monthMorePopover.scrollTop;
    }

    this.showEventPreview(eventRecord, event, { pinned: true });

    if (monthMorePopover) {
      this.restoreMonthMorePreviewScroll();
    }
  }

  restoreMonthMorePreviewScroll() {
    if (this.monthMorePreviewScrollTop === null) {
      return;
    }

    const scrollTop = this.monthMorePreviewScrollTop;
    const restore = () => {
      const popover = document.querySelector(
        ".discourse-events-calendar__month-more-popover"
      );

      if (popover) {
        popover.scrollTop = scrollTop;
      }
    };

    window.requestAnimationFrame(() => {
      restore();
      window.requestAnimationFrame(restore);
    });
  }

  @action
  showEventPreview(eventRecord, event, options = {}) {
    if (
      this.isMobileEventInteraction() &&
      this.eventTouchPreviewKey &&
      !options.fromTouch
    ) {
      return;
    }

    const trigger = event?.currentTarget;

    if (
      !options.pinned &&
      trigger?.closest?.(".discourse-events-calendar__month-more-popover")
    ) {
      return;
    }

    if (event?.type === "mouseenter" && !options.pinned && !options.immediate) {
      this.scheduleEventPreviewShow(eventRecord, event, options);
      return;
    }

    this.clearEventPreviewShowTimer();
    this.clearEventPreviewDismissTimer();
    this.selectedPreviewEvent = eventRecord;

    const monthDay = trigger?.closest?.(".discourse-events-calendar__month-day");
    const fromMonthMore = Boolean(
      trigger?.closest?.(".discourse-events-calendar__month-more-popover")
    );
    const fromTimeGrid = Boolean(
      trigger?.closest?.(
        ".discourse-events-calendar__time-grid-event, .discourse-events-calendar__time-grid-all-day-event"
      )
    );

    this.eventPreviewDate = monthDay?.dataset?.date || "";
    if (fromMonthMore) {
      this.eventPreviewSource = "month-overflow";
    } else if (fromTimeGrid && !options.pinned) {
      this.eventPreviewSource = "time-grid-hover";
    } else if (fromTimeGrid) {
      this.eventPreviewSource = "time-grid-pinned";
    } else if (this.eventPreviewDate && !options.pinned) {
      this.eventPreviewSource = "month-hover";
    } else if (this.eventPreviewDate) {
      this.eventPreviewSource = "month-pinned";
    } else if (!options.pinned) {
      this.eventPreviewSource = "event-hover";
    } else {
      this.eventPreviewSource = "default";
    }

    if (!fromMonthMore) {
      this.closeMonthMore();
    }

    const width = Math.min(360, Math.max(280, window.innerWidth - 24));
    const height = 320;
    const gap = fromTimeGrid ? 4 : 12;
    const targetRect = event?.currentTarget?.getBoundingClientRect?.();
    const pointerX = event?.clientX || targetRect?.right || window.innerWidth / 2;
    const pointerY = event?.clientY || targetRect?.top || 96;
    let left = targetRect ? targetRect.right + gap : pointerX + gap;
    let top = targetRect ? targetRect.top - 8 : pointerY - 24;

    if (left + width > window.innerWidth - gap) {
      left = targetRect ? targetRect.left - width - gap : pointerX - width - gap;
    }

    left = Math.max(gap, Math.min(left, window.innerWidth - width - gap));
    top = Math.max(gap, Math.min(top, window.innerHeight - height - gap));

    this.eventPreviewStyle = [
      `left: ${Math.round(left)}px`,
      `top: ${Math.round(top)}px`,
      `width: ${Math.round(width)}px`,
    ].join("; ");

    document.addEventListener("mousedown", this.handlePreviewDocumentMouseDown, true);
  }

  @action
  handlePreviewDocumentMouseDown(event) {
    const target = event.target;

    if (
      target?.closest?.(".discourse-events-calendar__event-popover") ||
      target?.closest?.(".discourse-events-calendar__month-more-popover") ||
      target?.closest?.(".discourse-events-calendar__month-more") ||
      target?.closest?.(".discourse-events-calendar__event-preview-trigger")
    ) {
      return;
    }

    this.dismissEventPreview();
  }

  @action
  closeEventPreview() {
    this.dismissEventPreview();
  }

  showAdjacentEventPreview(direction, event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    const events = this.previewNavigationEvents;
    const currentIndex = this.selectedPreviewEventIndex;

    if (events.length < 2 || currentIndex < 0) {
      return;
    }

    const nextIndex = (currentIndex + direction + events.length) % events.length;
    const nextEvent = events[nextIndex];

    this.clearEventPreviewDismissTimer();
    this.selectedPreviewEvent = nextEvent;
    this.eventPreviewDate = eventDateParts(nextEvent.starts_at).key;
  }

  @action
  showPreviousEventPreview(event) {
    this.showAdjacentEventPreview(-1, event);
  }

  @action
  showNextEventPreview(event) {
    this.showAdjacentEventPreview(1, event);
  }

  @action
  startEventPreviewSwipe(event) {
    if (!this.showEventPreviewNavigation) {
      return;
    }

    this.eventPreviewTouchStartX = event.touches?.[0]?.clientX ?? null;
  }

  @action
  finishEventPreviewSwipe(event) {
    if (!this.showEventPreviewNavigation || this.eventPreviewTouchStartX === null) {
      this.eventPreviewTouchStartX = null;
      return;
    }

    const endX = event.changedTouches?.[0]?.clientX;
    const deltaX = endX === undefined ? 0 : endX - this.eventPreviewTouchStartX;
    this.eventPreviewTouchStartX = null;

    if (Math.abs(deltaX) < 48) {
      return;
    }

    this.showAdjacentEventPreview(deltaX > 0 ? -1 : 1, event);
  }

  isMobileEventInteraction() {
    return window.matchMedia?.("(max-width: 700px)")?.matches === true;
  }

  clearEventTouchPreviewTimer() {
    if (!this.eventTouchPreviewTimer) {
      return;
    }

    window.clearTimeout(this.eventTouchPreviewTimer);
    this.eventTouchPreviewTimer = null;
  }

  @action
  startEventTouchPreview(eventRecord, event) {
    if (!this.isMobileEventInteraction()) {
      return;
    }

    this.clearEventTouchPreviewTimer();
    this.eventTouchLongPressFired = false;
    this.eventTouchPreviewKey = previewEventKey(eventRecord);
    this.eventTouchStartX = event.touches?.[0]?.clientX ?? null;
    this.eventTouchStartY = event.touches?.[0]?.clientY ?? null;

    this.eventTouchPreviewTimer = window.setTimeout(() => {
      this.eventTouchPreviewTimer = null;
      this.eventTouchLongPressFired = true;
      this.openEventDetail(eventRecord);
      this.suppressedEventClickKey = previewEventKey(eventRecord);
    }, 560);
  }

  @action
  moveEventTouchPreview(eventRecord, event) {
    if (!this.isMobileEventInteraction() || this.eventTouchStartX === null) {
      return;
    }

    const currentX = event.touches?.[0]?.clientX;
    const currentY = event.touches?.[0]?.clientY;

    if (currentX === undefined || currentY === undefined) {
      return;
    }

    if (
      Math.abs(currentX - this.eventTouchStartX) > 10 ||
      Math.abs(currentY - this.eventTouchStartY) > 10
    ) {
      this.clearEventTouchPreviewTimer();
      this.eventTouchPreviewKey = "";
    }
  }

  @action
  finishEventTouchPreview(eventRecord, event) {
    if (!this.isMobileEventInteraction()) {
      return;
    }

    this.clearEventTouchPreviewTimer();
    this.suppressedEventClickKey = previewEventKey(eventRecord);

    if (this.eventTouchLongPressFired) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      this.eventTouchLongPressFired = false;
      this.eventTouchPreviewKey = "";
      this.eventTouchStartX = null;
      this.eventTouchStartY = null;
      return;
    }

    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.showEventPreview(eventRecord, event, { pinned: true, fromTouch: true });
    this.eventTouchPreviewKey = "";
    this.eventTouchStartX = null;
    this.eventTouchStartY = null;
  }

  @action
  cancelEventTouchPreview() {
    this.clearEventTouchPreviewTimer();
    this.eventTouchLongPressFired = false;
    this.eventTouchPreviewKey = "";
    this.eventTouchStartX = null;
    this.eventTouchStartY = null;
  }

  @action
  preventEventContextMenu(event) {
    if (!this.isMobileEventInteraction()) {
      return;
    }

    event?.preventDefault?.();
    event?.stopPropagation?.();
  }

  @action
  keepEventPreviewOpen() {
    this.clearEventPreviewShowTimer();
    this.clearEventPreviewDismissTimer();
  }

  @action
  scheduleEventPreviewDismiss() {
    this.clearEventPreviewShowTimer();
    this.clearEventPreviewDismissTimer();

    this.eventPreviewDismissTimer = window.setTimeout(() => {
      this.eventPreviewDismissTimer = null;
      this.dismissEventPreview();
    }, EVENT_PREVIEW_HIDE_DELAY_MS);
  }

  @action
  maybeDismissMonthDayPreview(date) {
    this.clearEventPreviewShowTimer();

    if (this.eventPreviewSource !== "month-hover" || this.eventPreviewDate !== date) {
      return;
    }

    this.scheduleEventPreviewDismiss();
  }

  @action
  maybeDismissTimeGridPreview(eventRecord) {
    this.clearEventPreviewShowTimer();

    if (
      this.eventPreviewSource !== "time-grid-hover" ||
      previewEventKey(this.selectedPreviewEvent) !== previewEventKey(eventRecord)
    ) {
      return;
    }

    this.scheduleEventPreviewDismiss();
  }

  @action
  maybeDismissEventPreview(eventRecord) {
    this.clearEventPreviewShowTimer();

    if (
      this.eventPreviewSource !== "event-hover" ||
      previewEventKey(this.selectedPreviewEvent) !== previewEventKey(eventRecord)
    ) {
      return;
    }

    this.scheduleEventPreviewDismiss();
  }

  refreshSelectedPreviewEvent(eventRecord) {
    const key = previewEventKey(eventRecord);
    const refreshedEvent = this.events.find((candidate) => previewEventKey(candidate) === key);

    if (refreshedEvent) {
      this.selectedPreviewEvent = refreshedEvent;
    }
  }

  mergePreviewRsvpResponse(eventRecord, incomingEvent) {
    if (!incomingEvent) {
      return;
    }

    const targetKey = previewEventKey(eventRecord);
    const rsvpUpdates = {
      rsvp_counts: incomingEvent.rsvp_counts,
      current_user_rsvp_status: incomingEvent.current_user_rsvp_status,
      series_rsvp_counts: incomingEvent.series_rsvp_counts,
      series_current_user_rsvp_status: incomingEvent.series_current_user_rsvp_status,
      rsvp_occurrences: incomingEvent.rsvp_occurrences,
      capacity: incomingEvent.capacity,
      rsvp_enabled: incomingEvent.rsvp_enabled,
      rsvp_mode: incomingEvent.rsvp_mode,
      rsvp_ui_enabled: incomingEvent.rsvp_ui_enabled,
      rsvp_allow_interested: incomingEvent.rsvp_allow_interested,
    };
    const compactUpdates = Object.fromEntries(
      Object.entries(rsvpUpdates).filter(([, value]) => value !== undefined)
    );
    const currentEvents = asArray(this.model?.events);
    const events = currentEvents.map((event) =>
      previewEventKey(event) === targetKey ? { ...event, ...compactUpdates } : event
    );

    this.model = Array.isArray(this.model)
      ? events
      : {
          ...this.model,
          events,
        };
    this.resetEventsCaches();
    this.refreshSelectedPreviewEvent(eventRecord);
  }

  @action
  async rsvpPreviewEvent(eventRecord, event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (!eventRecord?.id || !eventRecord.canPreviewRsvp || this.isSavingPreviewRsvp) {
      return;
    }

    const data = { status: "going" };

    if (eventRecord.occurrence_id) {
      data.occurrence_id = eventRecord.occurrence_id;
    } else if (eventRecord.occurrence_key) {
      data.occurrence_key = eventRecord.occurrence_key;
    } else {
      data.scope = "series";
    }

    this.isSavingPreviewRsvp = true;
    this.previewRsvpSavingKey = previewEventKey(eventRecord);

    try {
      const response = await ajax(`/calendar/events/${eventRecord.id}/rsvp`, {
        type: "POST",
        data,
      });

      this.mergePreviewRsvpResponse(eventRecord, response?.event);
    } catch (error) {
      popupAjaxError(error);
    } finally {
      this.isSavingPreviewRsvp = false;
      this.previewRsvpSavingKey = "";
    }
  }

  @action
  hidePreviewEvent(eventRecord, event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (!eventRecord?.id) {
      return;
    }

    this.hiddenEventKeys = normalizedStringSet([
      ...this.hiddenEventKeys,
      previewEventKey(eventRecord),
    ]);
    this.saveClientEventPreferences();
    this.dismissEventPreview();
    this.closeMonthMore();
    this.resetEventsCaches();
  }

  @action
  updateTimeGridScrollCue(target) {
    const element = target?.currentTarget || target;

    if (!element) {
      return;
    }

    const maxScrollLeft = element.scrollWidth - element.clientWidth;
    const leftThreshold = Math.max(32, Math.min(160, element.clientWidth * 0.16));
    const grid = element.querySelector(".discourse-events-calendar__time-grid");

    grid?.style?.setProperty("--dec-time-grid-scroll-left", `${element.scrollLeft}px`);

    this.timeGridCanScrollLeft = element.scrollLeft > leftThreshold;
    this.timeGridCanScrollRight =
      maxScrollLeft > 16 && element.scrollLeft < maxScrollLeft - 16;
  }

  @action
  handleTimeGridWheel(event) {
    const horizontalDelta = event.deltaX || 0;

    if (Math.abs(horizontalDelta) < 1) {
      return;
    }

    const currentTarget = event.currentTarget;
    const scrollShell = currentTarget?.classList?.contains?.(
      "discourse-events-calendar__time-grid-scroll"
    )
      ? currentTarget
      : currentTarget?.closest?.(".discourse-events-calendar__time-grid-scroll");

    if (!scrollShell) {
      return;
    }

    const maxScrollLeft = scrollShell.scrollWidth - scrollShell.clientWidth;

    if (maxScrollLeft <= 0) {
      return;
    }

    const nextScrollLeft = Math.max(
      0,
      Math.min(maxScrollLeft, scrollShell.scrollLeft + horizontalDelta)
    );

    if (nextScrollLeft === scrollShell.scrollLeft) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    scrollShell.scrollLeft = nextScrollLeft;
    this.updateTimeGridScrollCue(scrollShell);
  }

  @action
  focusTimeGridWorkday(element) {
    if (!element) {
      return;
    }

    const signature = this.timeGridScrollFocusSignature;
    if (element.dataset.discourseEventsCalendarFocusSignature === signature) {
      return;
    }

    element.dataset.discourseEventsCalendarFocusSignature = signature;

    window.requestAnimationFrame(() => {
      if (element.scrollHeight <= element.clientHeight) {
        return;
      }

      const axis = element.querySelector(".discourse-events-calendar__time-grid-axis");
      const gridHeight = axis?.getBoundingClientRect?.().height || element.scrollHeight;
      const hourCount = Math.max(1, this.timeGridEndHour - this.timeGridStartHour);
      const hourHeight = gridHeight / hourCount;
      const preferredHour = Math.max(this.timeGridStartHour, Math.min(6, this.timeGridEndHour));
      const preferredTop = (preferredHour - this.timeGridStartHour) * hourHeight;
      const maxScrollTop = element.scrollHeight - element.clientHeight;

      element.scrollTop = Math.max(0, Math.min(preferredTop, maxScrollTop));
    });
  }

  @action
  startTimeGridDraft(date, event) {
    if (
      !this.canCreateEvents ||
      event.pointerType === "touch" ||
      event.button > 0 ||
      event.target?.closest?.("a, button, input, select, textarea")
    ) {
      return;
    }

    const gridStartMinutes = this.timeGridStartHour * 60;
    const gridEndMinutes = this.timeGridEndHour * 60;
    const startMinute = Math.min(
      timeGridMinuteFromPointer(event, event.currentTarget, gridStartMinutes, gridEndMinutes),
      gridEndMinutes - 15
    );

    event.preventDefault();
    this.dismissEventPreview();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    this.timeGridDraft = {
      date,
      pointerId: event.pointerId,
      anchorMinute: startMinute,
      startMinute,
      endMinute: Math.min(startMinute + 60, gridEndMinutes),
      hasMoved: false,
    };
  }

  @action
  moveTimeGridDraft(date, event) {
    const draft = this.timeGridDraft;

    if (!draft || draft.date !== date || draft.pointerId !== event.pointerId) {
      return;
    }

    const gridStartMinutes = this.timeGridStartHour * 60;
    const gridEndMinutes = this.timeGridEndHour * 60;
    const currentMinute = timeGridMinuteFromPointer(
      event,
      event.currentTarget,
      gridStartMinutes,
      gridEndMinutes
    );
    const startMinute = Math.min(draft.anchorMinute, currentMinute);
    const endMinute = Math.max(draft.anchorMinute, currentMinute);
    const range =
      endMinute - startMinute >= 15
        ? { startMinute, endMinute }
        : {
            startMinute: draft.anchorMinute,
            endMinute: Math.min(draft.anchorMinute + 60, gridEndMinutes),
          };

    event.preventDefault();
    this.timeGridDraft = {
      ...draft,
      ...range,
      hasMoved: true,
    };
  }

  @action
  finishTimeGridDraft(date, event) {
    const draft = this.timeGridDraft;

    if (!draft || draft.date !== date || draft.pointerId !== event.pointerId) {
      return;
    }

    const gridStartMinutes = this.timeGridStartHour * 60;
    const gridEndMinutes = this.timeGridEndHour * 60;
    const range = normalizeDraftRange(draft, gridStartMinutes, gridEndMinutes);

    event.preventDefault();
    event.currentTarget.releasePointerCapture?.(event.pointerId);

    if (!range || !draft.hasMoved) {
      this.timeGridDraft = null;
      return;
    }

    this.timeGridDraft = {
      ...draft,
      ...range,
      pointerId: null,
      isCommitted: true,
      hasMoved: true,
    };
  }

  @action
  cancelTimeGridDraft(date, event) {
    const draft = this.timeGridDraft;

    if (!draft || draft.date !== date || draft.pointerId !== event.pointerId) {
      return;
    }

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    this.timeGridDraft = null;
  }

  @action
  confirmTimeGridDraft(date, event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    const draft = this.timeGridDraft;
    if (!draft || draft.date !== date) {
      return;
    }

    const gridStartMinutes = this.timeGridStartHour * 60;
    const gridEndMinutes = this.timeGridEndHour * 60;
    const range = normalizeDraftRange(draft, gridStartMinutes, gridEndMinutes);

    if (!range) {
      this.timeGridDraft = null;
      return;
    }

    this.openCreateFormForDate(date, {
      startTime: timeValueFromMinutes(range.startMinute),
      endTime: timeValueFromMinutes(range.endMinute),
      endsNextDay: range.endMinute >= 1440,
    });
  }

  @action
  clearTimeGridDraft(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.timeGridDraft = null;
  }

  @action
  selectVenueDate(date) {
    this.view = "venue";
    this.setBrowserDay(parseDateString(date));
    this.selectQuickDate(date);
    this.createNotice = "";
  }

  @action
  selectDayDate(date) {
    this.view = "day";
    this.setBrowserDay(parseDateString(date));
    this.selectQuickDate(date);
    this.createNotice = "";
  }

  @action
  clearSelectedBrowserDate() {
    this.selectedBrowserDate = "";
  }

  @action
  updateLocationFilter(event) {
    this.location_id = event.target.value;

    if (this.room_id) {
      const selectedRoom = this.rooms.find((room) => `${room.id}` === `${this.room_id}`);
      if (selectedRoom && !this.roomMatchesLocationFilter(selectedRoom)) {
        this.room_id = "";
      }
    }

    this.selectedBrowserDate = "";
    this.loadEventsForCurrentState();
  }

  @action
  updateRoomFilter(event) {
    this.room_id = event.target.value;
    this.selectedBrowserDate = "";
    this.loadEventsForCurrentState();
  }

  @action
  updateCategoryFilter(event) {
    this.category_id = event.target.value;
    this.selectedBrowserDate = "";
    this.loadEventsForCurrentState();
  }

  @action
  updateTagFilter(event) {
    this.tag_id = event.target.value;
    this.tag_ids = "";
    this.selectedBrowserDate = "";
    this.loadEventsForCurrentState();
  }

  @action
  toggleTagFilter(tagId, event) {
    const nextTagIds = new Set(this.selectedTagIds);

    if (event.target.checked) {
      nextTagIds.add(`${tagId}`);
    } else {
      nextTagIds.delete(`${tagId}`);
    }

    this.tag_id = "";
    this.tag_ids = Array.from(nextTagIds).join(",");
    this.selectedBrowserDate = "";
    this.loadEventsForCurrentState();
  }

  @action
  toggleTagFilterMenu(event) {
    event?.preventDefault();
    this.isTagFilterMenuOpen = !this.isTagFilterMenuOpen;
  }

  @action
  closeTagFilterMenu() {
    this.isTagFilterMenuOpen = false;
  }

  @action
  clearTagFilter(event) {
    event?.preventDefault();
    this.tag_id = "";
    this.tag_ids = "";
    this.selectedBrowserDate = "";
    this.loadEventsForCurrentState();
  }

  @action
  toggleMutedCategory(categoryId, event) {
    const mutedCategoryIds = new Set(this.mutedCategoryIds);

    if (event.target.checked) {
      mutedCategoryIds.add(`${categoryId}`);
    } else {
      mutedCategoryIds.delete(`${categoryId}`);
    }

    this.mutedCategoryIds = normalizedStringSet(Array.from(mutedCategoryIds));
    this.selectedBrowserDate = "";
    this.saveClientEventPreferences();
    this.dismissEventPreview();
    this.closeMonthMore();
    this.resetEventsCaches();
  }

  @action
  toggleMutedTag(tagId, event) {
    const mutedTagIds = new Set(this.mutedTagIds);

    if (event.target.checked) {
      mutedTagIds.add(`${tagId}`);
    } else {
      mutedTagIds.delete(`${tagId}`);
    }

    this.mutedTagIds = normalizedStringSet(Array.from(mutedTagIds));
    this.selectedBrowserDate = "";
    this.saveClientEventPreferences();
    this.dismissEventPreview();
    this.closeMonthMore();
    this.resetEventsCaches();
  }

  @action
  hideVisibleEvents(event) {
    event?.preventDefault?.();

    const visibleKeys = this.events.map((visibleEvent) => previewEventKey(visibleEvent));

    if (visibleKeys.length === 0) {
      return;
    }

    this.hiddenEventKeys = normalizedStringSet([...this.hiddenEventKeys, ...visibleKeys]);
    this.selectedBrowserDate = "";
    this.saveClientEventPreferences();
    this.dismissEventPreview();
    this.closeMonthMore();
    this.resetEventsCaches();
  }

  @action
  clearHiddenEvents(event) {
    event?.preventDefault?.();

    this.hiddenEventKeys = [];
    this.saveClientEventPreferences();
    this.resetEventsCaches();
  }

  @action
  clearClientEventFilters(event) {
    event?.preventDefault?.();

    this.hiddenEventKeys = [];
    this.mutedCategoryIds = [];
    this.mutedTagIds = [];
    this.selectedBrowserDate = "";
    this.saveClientEventPreferences();
    this.dismissEventPreview();
    this.closeMonthMore();
    this.resetEventsCaches();
  }

  @action
  updateOrganizerFilter(event) {
    this.owner_id = event.target.value;
    this.selectedBrowserDate = "";
    this.loadEventsForCurrentState();
  }

  @action
  updateEventVisibilityFilter(event) {
    this.event_visibility = normalizeEventVisibility(event.target.value);
    this.selectedBrowserDate = "";
    this.loadEventsForCurrentState();
  }

  @action
  updateRsvpStatusFilter(event) {
    this.rsvp_status = normalizeRsvpStatus(event.target.value);
    this.selectedBrowserDate = "";
    this.loadEventsForCurrentState();
  }

  @action
  updateSearchFilter(event) {
    this.searchQuery = event?.target?.value ?? this.searchQuery;
    this.scheduleSearchCommit();
  }

  @action
  clearBrowserFilters() {
    this.filter = "upcoming";
    this.q = "";
    this.searchQuery = "";
    this.room_id = "";
    this.location_id = "";
    this.category_id = "";
    this.tag_id = "";
    this.tag_ids = "";
    this.owner_id = "";
    this.event_visibility = "";
    this.scope = "all";
    this.rsvp_status = "";
    this.selectedBrowserDate = "";
    this.isTagFilterMenuOpen = false;
    this.loadEventsForCurrentState();
  }

  @action
  toggleCreateForm() {
    if (this.isCreateFormExpanded) {
      this.closeCreateForm();
    } else {
      this.closeCreateMenu();
      this.openCreateForm();
    }
  }

  openCreateForm() {
    this.resetForm();
    this.timeGridDraft = null;
    this.isCreateFormExpanded = true;
    this.create = "1";
    this.createNotice = "";
    this.createError = "";
    this.createConflict = null;
    this.createConflictAlternatives = [];
  }

  @action
  toggleCreateMenu(event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isCreateMenuOpen) {
      this.closeCreateMenu();
    } else {
      this.isCreateMenuOpen = true;
      document.addEventListener("mousedown", this.handleCreateMenuDocumentMouseDown, true);
    }
  }

  @action
  closeCreateMenu() {
    this.isCreateMenuOpen = false;
    document.removeEventListener("mousedown", this.handleCreateMenuDocumentMouseDown, true);
  }

  @action
  handleCreateMenuDocumentMouseDown(event) {
    if (
      event.target?.closest?.(".discourse-events-calendar__create-menu") ||
      event.target?.closest?.(".discourse-events-calendar__create-menu-trigger")
    ) {
      return;
    }

    this.closeCreateMenu();
  }

  @action
  openCreateEventFromMenu() {
    this.closeCreateMenu();
    this.openCreateForm();
  }

  @action
  closeCreateForm() {
    this.closeCreateMenu();
    this.teardownEventDescriptionUpload();
    this.isCreateFormExpanded = false;
    this.create = "";
    this.timeGridDraft = null;
    this.showRoomForm = false;
    this.resetRoomForm();
    this.resetForm();
    this.createNotice = "";
    this.createConflict = null;
  }

  // Called from the route's resetController on isExiting. Symmetrically
  // tears down any document-level listeners that close handlers (which may
  // never fire if the user navigates away mid-popover) would otherwise
  // leave attached. Without this, every visit to the calendar after closing
  // a popover via navigation would install another listener.
  cleanupOpenPopovers() {
    this.clearTransientSubscribeUi({ clearQueryParam: true });
    this.closeExternalCalendarPanel();

    if (this.openMonthMoreDate) {
      this.closeMonthMore();
    }
    if (this.selectedPreviewEvent) {
      this.dismissEventPreview();
    }
    if (this.isCreateMenuOpen) {
      this.closeCreateMenu();
    }
  }

  @action
  focusCreateDialog(element) {
    element?.focus?.();
  }

  @action
  handleCreateDialogKeydown(event) {
    if (event.key === "Escape" && !this.isSaving) {
      this.closeCreateForm();
    }
  }

  @action
  addEventOnDate(date) {
    this.openCreateFormForDate(date);
  }

  @action
  addEventForVenueColumn(column, event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    this.openCreateFormForDate(this.activeRangeStart);

    if (column?.roomId) {
      const room = this.rooms.find((candidate) => `${candidate.id}` === `${column.roomId}`);
      this.applyRoomToCreateForm(room);
    }
  }

  @action
  selectQuickDate(date) {
    this.eventDate = date;
    this.calendarMonth = monthString(parseDateString(date));
    this.ensureWeeklyBydaySelection();

    if (
      this.isRecurring &&
      (!this.recurrenceUntil || dateIsBefore(this.recurrenceUntil, date))
    ) {
      this.recurrenceUntil = defaultRecurrenceUntil(date);
    }

    this.scheduleRoomAvailabilityCheck();
    this.scheduleVenueSuggestionsLoad();
  }

  @action
  selectCalendarDate(date) {
    this.selectQuickDate(date);
  }

  @action
  updateEventDate(event) {
    this.selectQuickDate(event.target.value);
  }

  @action
  previousMonth() {
    const month = parseDateString(this.calendarMonth);
    month.setMonth(month.getMonth() - 1);
    this.calendarMonth = monthString(month);
  }

  @action
  nextMonth() {
    const month = parseDateString(this.calendarMonth);
    month.setMonth(month.getMonth() + 1);
    this.calendarMonth = monthString(month);
  }

  @action
  updateStartTime(event) {
    this.startTime = event.target.value;

    if (!this.hasValidTimeRange) {
      this.setDefaultEndTime();
    }

    this.scheduleRoomAvailabilityCheck();
    this.scheduleVenueSuggestionsLoad();
  }

  @action
  updateEndTime(event) {
    this.endTime = event.target.value;
    this.scheduleRoomAvailabilityCheck();
    this.scheduleVenueSuggestionsLoad();
  }

  @action
  updateTimezone(event) {
    this.timezone = normalizeTimeZone(event.target.value, browserTimeZone());
    this.scheduleRoomAvailabilityCheck();
    this.scheduleVenueSuggestionsLoad();
  }

  @action
  updateDescription(valueOrEvent) {
    this.description = editorValue(valueOrEvent);
  }

  @action
  async uploadEventImage(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    this.imageUploadError = "";
    this.isUploadingImage = true;

    try {
      const upload = await uploadImageFile(file);
      this.imageUpload = upload;
      this.imageUploadId = upload?.id ? `${upload.id}` : "";
      this.pendingAiImageUpload = null;
    } catch (error) {
      this.imageUploadError = extractErrorMessage(error, "Image could not be uploaded.");
      popupAjaxError(error);
    } finally {
      this.isUploadingImage = false;
    }
  }

  @action
  removeEventImage() {
    this.imageUpload = null;
    this.imageUploadId = "";
    this.imageUploadError = "";
    this.pendingAiImageUpload = null;
  }

  @action
  async openAiImagePrompt(event) {
    event?.preventDefault?.();

    if (this.cannotComposeAiImagePrompt) {
      return;
    }

    if (this.aiImagePrompt.trim()) {
      this.showAiImagePrompt = true;
      this.imageUploadError = "";
      return;
    }

    await this.composeAiImagePrompt();
  }

  @action
  updateAiImagePrompt(event) {
    this.aiImagePrompt = event.target.value;
  }

  @action
  async resetAiImagePrompt(event) {
    event?.preventDefault?.();

    this.aiImagePrompt = "";
    await this.composeAiImagePrompt();
  }

  @action
  async regenerateAiImage(event) {
    event?.preventDefault?.();

    this.pendingAiImageUpload = null;

    if (!this.aiImagePrompt.trim()) {
      await this.composeAiImagePrompt();
    }

    if (!this.aiImagePrompt.trim()) {
      return;
    }

    await this.generateAiImage(event);
  }

  @action
  acceptAiImage(event) {
    event?.preventDefault?.();

    if (!this.pendingAiImageUpload) {
      return;
    }

    this.imageUpload = this.pendingAiImageUpload;
    this.imageUploadId = this.imageUpload?.id ? `${this.imageUpload.id}` : "";
    this.pendingAiImageUpload = null;
    this.imageUploadError = "";
    this.showAiImagePrompt = false;
  }

  @action
  discardAiImage(event) {
    event?.preventDefault?.();

    this.pendingAiImageUpload = null;
    this.imageUploadError = "";
  }

  @action
  async generateAiImage(event) {
    event?.preventDefault?.();

    if (this.cannotGenerateAiImage) {
      return;
    }

    this.isGeneratingAiImage = true;
    this.imageUploadError = "";

    try {
      const response = await ajax("/calendar/events/generate_image.json", {
        type: "POST",
        data: {
          prompt: this.aiImagePrompt,
          event: this.aiImagePromptEventData(),
        },
      });
      const upload = response?.upload;
      this.pendingAiImageUpload = upload || null;
      this.showAiImagePrompt = false;
    } catch (error) {
      this.imageUploadError = extractErrorMessage(error, "AI image could not be generated.");
      popupAjaxError(error);
    } finally {
      this.isGeneratingAiImage = false;
    }
  }

  async composeAiImagePrompt() {
    this.isComposingAiImagePrompt = true;
    this.imageUploadError = "";

    try {
      const response = await ajax("/calendar/events/image_prompt.json", {
        type: "POST",
        data: {
          event: this.aiImagePromptEventData(),
        },
      });

      this.aiImagePrompt = response?.prompt || "";
      this.showAiImagePrompt = this.aiImagePrompt.trim().length > 0;
    } catch (error) {
      this.imageUploadError = extractErrorMessage(
        error,
        "AI image prompt could not be prepared."
      );
    } finally {
      this.isComposingAiImagePrompt = false;
    }
  }

  aiImagePromptEventData() {
    const room = this.rooms.find((candidate) => `${candidate.id}` === `${this.selectedRoomId}`);
    const tagNames = [...this.pendingCreateTagNames, this.normalizedNewTagName]
      .map((name) => name.trim())
      .filter(Boolean)
      .filter((name, index, names) => names.indexOf(name) === index);

    return {
      title: this.title.trim(),
      description: this.description.trim(),
      starts_at: this.availabilityStartParam(),
      ends_at: this.availabilityEndParam(),
      timezone: normalizeTimeZone(this.timezone, browserTimeZone()),
      location: this.location.trim(),
      venue_type: this.venueType,
      meeting_url: normalizedUrlInput(this.meetingUrl),
      custom_location_url:
        this.venueType === "custom" && looksLikeUrl(this.location)
          ? normalizedUrlInput(this.location)
          : "",
      category_id: this.selectedCategoryId,
      room_id: this.selectedRoomId,
      room_name: room?.name || "",
      place_id: this.placeId,
      place_name: this.placeQuery,
      tag_ids: this.selectedCreateTagIds,
      tag_names: tagNames.join(","),
      event_roles: [
        ...participantRoleRows(this.hostNames, "host", 0),
        ...participantRoleRows(this.coHostNames, "co_host", 100),
        ...participantRoleRows(this.speakerNames, "speaker", 200),
        ...participantRoleRows(this.withNames, "custom_host", 300),
      ],
    };
  }

  @action
  async uploadRoomImage(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    this.roomImageUploadError = "";
    this.isUploadingRoomImage = true;

    try {
      const upload = await uploadImageFile(file);
      this.roomImageUpload = upload;
      this.roomImageUploadId = upload?.id ? `${upload.id}` : "";
    } catch (error) {
      this.roomImageUploadError = extractErrorMessage(error, "Room image could not be uploaded.");
      popupAjaxError(error);
    } finally {
      this.isUploadingRoomImage = false;
    }
  }

  @action
  removeRoomImage() {
    this.roomImageUpload = null;
    this.roomImageUploadId = "";
    this.roomImageUploadError = "";
  }

  ensureEventDescriptionUpload() {
    if (!this.eventDescriptionUpload) {
      this.eventDescriptionUpload = new UppyComposerUpload(getOwner(this), {
        composerEventPrefix: "discourse-events-calendar:event-description",
        composerModel: { privateMessage: false },
        uploadMarkdownResolvers: [],
        uploadPreProcessors: [],
        uploadHandlers: [],
        fileUploadElementId: this.descriptionFileUploadElementId,
      });
      this.eventDescriptionUpload.uppyId = "event-description-uppy";
    }

    return this.eventDescriptionUpload;
  }

  setupEventDescriptionUploadIfReady() {
    if (this.eventDescriptionUploadSetup || !this.eventDescriptionUploadElement) {
      return;
    }

    if (!document.getElementById(this.descriptionFileUploadElementId)) {
      return;
    }

    const upload = this.ensureEventDescriptionUpload();

    if (!upload.textManipulation) {
      return;
    }

    upload.setup(this.eventDescriptionUploadElement);
    this.eventDescriptionUploadSetup = true;
  }

  @action
  setupEventDescriptionEditor(textManipulation) {
    const upload = this.ensureEventDescriptionUpload();
    upload.textManipulation = textManipulation;
    this.setupEventDescriptionUploadIfReady();
  }

  @action
  setupEventDescriptionUpload(element) {
    this.eventDescriptionUploadElement = element;
    this.setupEventDescriptionUploadIfReady();
  }

  @action
  registerEventDescriptionFileInput() {
    this.setupEventDescriptionUploadIfReady();
  }

  @action
  teardownEventDescriptionUpload() {
    if (this.eventDescriptionUploadSetup && this.eventDescriptionUpload) {
      this.eventDescriptionUpload.teardown();
    }

    this.eventDescriptionUpload = null;
    this.eventDescriptionUploadElement = null;
    this.eventDescriptionUploadSetup = false;
  }

  @action
  eventDescriptionExtraButtons(toolbar) {
    if (!this.currentUser || !this.site?.desktopView) {
      return;
    }

    toolbar.addButton({
      id: "upload",
      group: "insertions",
      icon: this.eventDescriptionUploadIcon,
      title: "upload",
      sendAction: this.openEventDescriptionFilePicker,
    });
  }

  @action
  openEventDescriptionFilePicker() {
    document.getElementById(this.descriptionFileUploadElementId)?.click();
  }

  @action
  toggleAllDay(event) {
    this.allDay = event.target.checked;
    this.clearRoomAvailability();
    this.scheduleRoomAvailabilityCheck();
    this.scheduleVenueSuggestionsLoad();
  }

  @action
  toggleEndsNextDay(event) {
    this.endsNextDay = event.target.checked;

    if (!this.hasValidTimeRange) {
      this.setDefaultEndTime();
    }

    this.scheduleRoomAvailabilityCheck();
    this.scheduleVenueSuggestionsLoad();
  }

  @action
  toggleRsvpEnabled(event) {
    this.rsvpEnabled = event.target.checked;
  }

  @action
  updateRsvpMode(event) {
    this.rsvpMode = event.target.value === "external" ? "external" : "native";
  }

  @action
  updateExternalRsvpUrl(event) {
    this.externalRsvpUrl = event.target.value;
  }

  @action
  toggleRsvpAllowInterested(event) {
    this.rsvpAllowInterested = event.target.checked;
  }

  @action
  updateCapacity(event) {
    this.capacity = event.target.value;
    this.scheduleVenueSuggestionsLoad();
  }

  @action
  toggleCreateTopic(event) {
    this.createTopic = event.target.checked;
    this.createChat = false;
    this.conversationMode = this.createTopic ? "discussion" : "none";
  }

  @action
  updateConversationMode(event) {
    this.conversationMode = normalizeConversationMode(event.target.value);
    if (this.conversationMode === "chat" && !this.canUseChatThread) {
      this.conversationMode = "discussion";
    }
    this.createTopic = this.conversationMode === "discussion";
    this.createChat = this.conversationMode === "chat";
  }

  @action
  updateVisibility(event) {
    this.visibility = event.target.value;
  }

  @action
  updateCategory(event) {
    this.selectedCategoryId = event.target.value;
    this.syncVenueSelectionForCategory();

    if (this.showVenueSearch && this.placeQuery.trim().length >= 2) {
      debounce(this, this.loadPlaceOptions, this.placeQuery, 250);
    }

    this.scheduleVenueSuggestionsLoad();
  }

  @action
  updateTagNames(event) {
    this.tagNamesCsv = event.target.value;
  }

  @action
  toggleCreateTagMenu(event) {
    event?.preventDefault();
    this.isCreateTagMenuOpen = !this.isCreateTagMenuOpen;
  }

  @action
  toggleCreateTag(tagId, event) {
    const nextTagIds = new Set(this.selectedCreateTagIds);

    if (event.target.checked) {
      nextTagIds.add(`${tagId}`);
    } else {
      nextTagIds.delete(`${tagId}`);
    }

    this.createTagIds = Array.from(nextTagIds).join(",");
  }

  @action
  removeCreateTag(tagId, event) {
    event?.preventDefault();
    const nextTagIds = this.selectedCreateTagIds.filter((id) => id !== `${tagId}`);
    this.createTagIds = nextTagIds.join(",");
  }

  @action
  removePendingCreateTag(name, event) {
    event?.preventDefault();
    this.tagNamesCsv = this.pendingCreateTagNames
      .filter((tagName) => tagName !== name)
      .join(",");
  }

  @action
  updateNewTagName(event) {
    this.newTagName = event.target.value;
  }

  @action
  handleNewTagKeydown(event) {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    this.addCreateTag();
  }

  @action
  addCreateTag(event) {
    event?.preventDefault();
    this.applyNewCreateTagName();
  }

  @action
  toggleCreatePeopleDetails(event) {
    event?.preventDefault();
    this.showCreatePeopleDetails = !this.showCreatePeopleDetails;
  }

  @action
  updateHostNames(event) {
    this.hostNames = event.target.value;
    debounce(this, this.loadRoleUserOptions, this.hostNames, 250);
  }

  @action
  updateCoHostNames(event) {
    this.coHostNames = event.target.value;
    debounce(this, this.loadRoleUserOptions, this.coHostNames, 250);
  }

  @action
  updateSpeakerNames(event) {
    this.speakerNames = event.target.value;
    debounce(this, this.loadRoleUserOptions, this.speakerNames, 250);
  }

  @action
  updateWithNames(event) {
    this.withNames = event.target.value;
    debounce(this, this.loadRoleUserOptions, this.withNames, 250);
  }

  @action
  updateInviteUsernames(event) {
    this.inviteUsernames = event.target.value;
  }

  @action
  updateInviteGroupNames(event) {
    this.inviteGroupNames = event.target.value;
  }

  applyNewCreateTagName() {
    const tagName = this.normalizedNewTagName;

    if (!tagName) {
      return;
    }

    const matchingTag = this.availableTags.find(
      (tag) => normalizeTagName(tag.name) === tagName
    );

    if (matchingTag) {
      const nextTagIds = new Set(this.selectedCreateTagIds);
      nextTagIds.add(`${matchingTag.id}`);
      this.createTagIds = Array.from(nextTagIds).join(",");
    } else {
      this.tagNamesCsv = [...this.pendingCreateTagNames, tagName]
        .filter((name, index, names) => names.indexOf(name) === index)
        .join(",");
    }

    this.newTagName = "";
  }

  @action
  updateRecurrenceRule(event) {
    this.recurrenceRule = event.target.value;

    if (this.isRecurring && !this.recurrenceUntil) {
      this.recurrenceUntil = defaultRecurrenceUntil(this.eventDate);
    }

    if (this.isWeeklyRecurrence) {
      this.ensureWeeklyBydaySelection();
    }

    if (!this.isRecurring) {
      this.recurrenceInterval = "1";
      this.recurrenceUntil = "";
      this.recurrenceByday = "";
    }
  }

  @action
  updateRecurrenceInterval(event) {
    this.recurrenceInterval = `${normalizedInterval(event.target.value)}`;
  }

  @action
  toggleRecurrenceByday(weekday, event) {
    const selected = new Set(this.selectedRecurrenceByday);

    if (event.target.checked) {
      selected.add(weekday.code);
    } else {
      selected.delete(weekday.code);
    }

    this.recurrenceByday = RECURRENCE_WEEKDAYS
      .map((day) => day.code)
      .filter((code) => selected.has(code))
      .join(",");
  }

  ensureWeeklyBydaySelection() {
    if (!this.isWeeklyRecurrence || this.recurrenceByday) {
      return;
    }

    this.recurrenceByday = weekdayCodeForDate(this.eventDate);
  }

  @action
  toggleRoomForm() {
    this.showRoomForm = !this.showRoomForm;

    if (this.showRoomForm && !this.roomLocation) {
      this.roomLocation = this.selectedLocationRoomName();
    }
  }

  @action
  cancelRoomForm() {
    this.showRoomForm = false;
    this.resetRoomForm();
  }

  @action
  async createRoom(event) {
    event.preventDefault();

    if (!this.canCreateRoom) {
      return;
    }

    this.isSavingRoom = true;
    this.roomError = "";

    const room = {
      name: this.roomName.trim(),
      location: this.roomLocation.trim() || this.selectedLocationRoomName(),
      image_upload_id: this.roomImageUploadId,
    };

    if (this.selectedLocationId) {
      room.location_id = this.selectedLocationId;
    }

    if (this.roomCapacity) {
      room.capacity = this.roomCapacity;
    }

    if (this.roomBookingWindowDays) {
      room.booking_window_days = this.roomBookingWindowDays;
    }

    if (this.roomBufferMinutes) {
      room.buffer_minutes = this.roomBufferMinutes;
    }

    try {
      const response = await ajax("/calendar/rooms.json", {
        type: "POST",
        data: { room },
      });
      const createdRoom = response?.room || response;
      const rooms = [...this.rooms, createdRoom].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      this.model = { ...this.model, rooms };
      this.selectedRoomId = `${createdRoom.id}`;
      const matchingLocation = this.selectedLocationForRoom(createdRoom);
      if (matchingLocation) {
        this.selectedLocationId = locationValue(matchingLocation);
      }
      if (createdRoom.location) {
        this.location = matchingLocation
          ? this.selectedLocationPublicAddress(matchingLocation)
          : createdRoom.location;
        this.roomLocationApplied = this.location;
      }
      this.scheduleRoomAvailabilityCheck();
      this.resetRoomForm();
      this.showRoomForm = false;
    } catch (error) {
      this.roomError = extractErrorMessage(error);
      popupAjaxError(error);
    } finally {
      this.isSavingRoom = false;
    }
  }

  @action
  async createEvent(event) {
    event.preventDefault();

    if (!this.canSubmitEvent) {
      return;
    }

    this.isSaving = true;
    this.createError = "";
    this.createNotice = "";
    this.createConflict = null;
    this.applyNewCreateTagName();

    const customRrule = this.customWeeklyRrule;
    const venueType = normalizeVenueType(this.venueType);
    const meetingUrl = venueType === "virtual" ? normalizedUrlInput(this.meetingUrl) : "";
    const customLocation = venueType === "custom" ? this.location.trim() : "";
    const publicLocation =
      venueType === "virtual"
        ? "Virtual event"
        : venueType === "tbd"
          ? "Venue TBD"
          : this.location.trim();
    const eventData = {
      title: this.title.trim(),
      status: event?.submitter?.dataset?.status || "published",
      starts_at: this.availabilityStartParam(),
      ends_at: this.availabilityEndParam(),
      location: publicLocation,
      venue_type: venueType,
      meeting_url: meetingUrl,
      custom_location_url:
        venueType === "custom" && looksLikeUrl(customLocation)
          ? normalizedUrlInput(customLocation)
          : "",
      description: this.description.trim(),
      image_upload_id: this.imageUploadId,
      timezone: normalizeTimeZone(this.timezone, browserTimeZone()),
      visibility: visibilityValueForMode(this.visibility),
      hidden: hiddenValueForMode(this.visibility),
      recurrence_rule: customRrule ? "none" : this.recurrenceRule,
      recurrence_interval: this.isRecurring ? normalizedInterval(this.recurrenceInterval) : 1,
      recurrence_until: this.isRecurring ? `${this.recurrenceUntil}T23:59:59` : "",
      rsvp_enabled: this.rsvpEnabled,
      rsvp_mode: this.rsvpEnabled ? this.rsvpMode : "native",
      external_rsvp_url:
        this.rsvpEnabled && this.rsvpMode === "external"
          ? normalizedUrlInput(this.externalRsvpUrl)
          : "",
      rsvp_allow_interested:
        this.rsvpEnabled && this.rsvpMode !== "external" ? this.rsvpAllowInterested : false,
      create_topic: this.createTopic,
      create_chat_thread: this.createChat,
      conversation_mode: this.conversationMode,
    };

    if (customRrule) {
      eventData.external_rrule = customRrule;
    }

    const eventRoles = [
      ...participantRoleRows(this.hostNames, "host", 0),
      ...participantRoleRows(this.coHostNames, "co_host", 100),
      ...participantRoleRows(this.speakerNames, "speaker", 200),
      ...participantRoleRows(this.withNames, "custom_host", 300),
    ];

    if (eventRoles.length > 0) {
      eventData.event_roles = eventRoles;
    }

    if (this.capacity) {
      eventData.capacity = this.capacity;
    }

    if (this.selectedCategoryId) {
      eventData.category_id = this.selectedCategoryId;
    } else if (this.model?.defaults?.category_id) {
      eventData.category_id = this.model.defaults.category_id;
    }

    if (this.selectedCreateTagIds.length > 0) {
      eventData.tag_ids = this.selectedCreateTagIds;
    }

    if (this.pendingCreateTagNames.length > 0) {
      eventData.tag_names = this.pendingCreateTagNames.join(",");
    }

    if (venueType === "existing" && this.selectedRoomId) {
      eventData.room_id = this.selectedRoomId;
    }

    if (venueType === "existing" && this.selectedLocationId) {
      eventData.location_id = this.selectedLocationId;
    }

    if (venueType === "existing" && this.placesEnabled && this.placeId) {
      eventData.place_id = this.placeId;
    }

    try {
      const createdEvent = await ajax("/calendar/events.json", {
        type: "POST",
        data: {
          event: eventData,
        },
      });

      const eventRecord = createdEvent?.event || createdEvent;
      await this.createInitialInvitations(eventRecord);
      this.resetForm();
      this.teardownEventDescriptionUpload();
      this.timeGridDraft = null;
      this.isCreateFormExpanded = false;
      this.create = "";
      this.router.transitionTo("discourse-events-calendar-event", eventRecord.id);
    } catch (error) {
      const responseJSON = error?.jqXHR?.responseJSON || error?.responseJSON;
      this.createConflict = responseJSON?.conflicts?.[0] || responseJSON?.conflict || null;
      this.createConflictAlternatives = responseJSON?.suggested_alternatives || [];
      this.createError = extractErrorMessage(error);
      this.isCreateFormExpanded = true;
      this.create = "1";
      popupAjaxError(error);
    } finally {
      this.isSaving = false;
    }
  }

  async createInitialInvitations(eventRecord) {
    if (!eventRecord?.id || !this.hasCreateInvitees) {
      return;
    }

    try {
      await ajax(`/calendar/events/${encodeURIComponent(eventRecord.id)}/invitations.json`, {
        type: "POST",
        data: {
          invitation: {
            usernames: this.inviteUsernames,
            group_names: this.inviteGroupNames,
          },
        },
      });
    } catch (error) {
      popupAjaxError(error);
    }
  }
}
